import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Program, getWeekWorkouts } from "@/lib/mock-data";
import { usePreferences } from "@/hooks/usePreferences";
import { usePlaylistManager } from "@/hooks/usePlaylistManager";
import { useWorkoutSchedule } from "@/hooks/useWorkoutSchedule";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { BodyWeightLog } from "@shared/schema";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { isOnline, queueProgramJoin, queueProgramLeave } from "@/lib/offlineSync";
import { Dumbbell, Flame, Timer, Play, Plus, Activity, LayoutDashboard, Trash2, Calendar, AlertTriangle, Target, X, HelpCircle, ChevronRight, ChevronDown, TrendingUp, Zap, Award, Search, Star, Eye, EyeOff, Archive, Pause, RotateCcw, Scale, Layers, Moon, Music, Heart, Coffee, CalendarCheck, Clock, ExternalLink, Ban, MoreVertical, Settings, CheckCircle } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { MobileNav } from "@/components/ui/mobile-nav";
import { LayoutWithSidebar } from "@/components/layout-with-sidebar";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { format, differenceInWeeks, differenceInDays, startOfWeek, isThisWeek, subWeeks, isSameDay, parseISO } from "date-fns";
import { useTrainingBlocks, CATEGORY_COLORS, CATEGORY_ICONS, CATEGORY_CALENDAR_COLORS, TrainingBlocksDialog } from "@/components/training-blocks";
import { getActiveWorkout, setActiveWorkout, clearActiveWorkout, type ActiveWorkout } from "@/lib/activeWorkoutStore";
import type { TrainingBlockCategory } from "@shared/schema";
import { useDrafts, type ProgramDraft } from "@/hooks/useDrafts";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { InstallPromptBanner } from "@/components/install-prompt-banner";
import { ProgramImage } from "@/components/program-image";
import type { CustomProgram, ProgramStatus, ProgramMembership } from "@/types/program";
import { loadProgramMemberships, saveProgramMemberships, detectProgramModifications } from "@/lib/membership-storage";


export default function Home() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const isAdmin = (user as any)?.role === "admin";
  const isCoach = (user as any)?.role === "coach" || isAdmin;
  const userId = (user as any)?.id || "guest";
  const { weightUnit, isUpdating } = usePreferences();
  const {
    customPlaylists,
    hiddenCuratedGenres,
    isCuratedVisible,
    CURATED_PLAYLISTS,
  } = usePlaylistManager();
  const [selectedPlaylistId, setSelectedPlaylistId] = useState<string>(() => {
    const stored = localStorage.getItem('myFitBeats_selectedPlaylistId');
    return stored || "hip-hop";
  });
  
  useEffect(() => {
    localStorage.setItem('myFitBeats_selectedPlaylistId', selectedPlaylistId);
  }, [selectedPlaylistId]);

  const { data: weightLogsData = [] } = useQuery<BodyWeightLog[]>({
    queryKey: ['/api/body-weight-logs'],
    enabled: !!user,
    staleTime: 60 * 1000,
    placeholderData: (prev) => prev,
  });
  
  // Cache weight logs to prevent flash during auth refresh - use localStorage for persistence
  const getInitialWeightLogs = (): BodyWeightLog[] => {
    try {
      const cached = localStorage.getItem('myFitBeats_cachedWeightLogs');
      if (cached) return JSON.parse(cached);
    } catch {}
    return [];
  };
  const lastKnownWeightLogsRef = useRef<BodyWeightLog[]>(getInitialWeightLogs());
  const weightLogs = useMemo(() => {
    if (weightLogsData && weightLogsData.length > 0) {
      lastKnownWeightLogsRef.current = weightLogsData;
      try {
        localStorage.setItem('myFitBeats_cachedWeightLogs', JSON.stringify(weightLogsData));
      } catch {}
      return weightLogsData;
    }
    // Return cached data during auth refresh
    if (lastKnownWeightLogsRef.current.length > 0) {
      return lastKnownWeightLogsRef.current;
    }
    // Try localStorage fallback
    try {
      const cached = localStorage.getItem('myFitBeats_cachedWeightLogs');
      if (cached) {
        const logs = JSON.parse(cached);
        if (Array.isArray(logs) && logs.length > 0) {
          lastKnownWeightLogsRef.current = logs;
          return logs;
        }
      }
    } catch {}
    return [];
  }, [weightLogsData]);
  
  // Type for completed workout entries
  type CompletedWorkout = { 
    date: string; 
    programId: string | null; 
    programName: string | null; 
    sectionName: string | null;
    week?: number | null;
    workoutIndex?: number | null;
    sessionId?: string | null;
    instanceId?: string | null;
  };
  
  // Fetch completed workouts from database for stats (replaces localStorage)
  const { data: completedWorkoutsData } = useQuery<{ completedWorkouts: CompletedWorkout[] }>({
    queryKey: ['/api/workout-logs/completed'],
    enabled: !!user,
    staleTime: 60 * 1000, // 1 minute - reasonable cache time
    refetchOnMount: 'always', // Always refetch when returning to home page (ensures stats update after workout)
    placeholderData: (prev) => prev, // Keep previous data during refetch to prevent flash
  });
  
  // Keep track of last known completed workouts - use localStorage for persistence
  const getInitialCompletedWorkouts = (): CompletedWorkout[] => {
    try {
      const cached = localStorage.getItem('myFitBeats_cachedCompletedWorkouts');
      if (cached) return JSON.parse(cached);
    } catch {}
    return [];
  };
  const lastKnownWorkoutsRef = useRef<CompletedWorkout[]>(getInitialCompletedWorkouts());
  
  // For logged-in users, use database data. For guests, fallback to localStorage
  const dbCompletedWorkouts: CompletedWorkout[] = useMemo(() => {
    // If we have fresh data from the API, use it and cache it
    if (completedWorkoutsData?.completedWorkouts && completedWorkoutsData.completedWorkouts.length > 0) {
      lastKnownWorkoutsRef.current = completedWorkoutsData.completedWorkouts;
      // Persist to localStorage for recovery after query clears
      try {
        localStorage.setItem('myFitBeats_cachedCompletedWorkouts', JSON.stringify(completedWorkoutsData.completedWorkouts));
      } catch {}
      return completedWorkoutsData.completedWorkouts;
    }
    
    // If user exists but API data is empty/loading, use cached data to prevent flash to zeros
    if (lastKnownWorkoutsRef.current.length > 0) {
      return lastKnownWorkoutsRef.current;
    }
    
    // Try localStorage fallback for logged-in users
    try {
      const cached = localStorage.getItem('myFitBeats_cachedCompletedWorkouts');
      if (cached) {
        const workouts = JSON.parse(cached);
        if (Array.isArray(workouts) && workouts.length > 0) {
          lastKnownWorkoutsRef.current = workouts;
          return workouts;
        }
      }
    } catch {}
    
    // Final fallback to old localStorage format for guests
    if (!user) {
      try {
        const calendarData = localStorage.getItem("myFitBeats_global_calendar");
        if (calendarData) {
          const { completedWorkouts = [] } = JSON.parse(calendarData);
          return completedWorkouts.map((w: any) => ({
            date: w.date,
            programId: w.programId || null,
            programName: w.programName || null,
            sectionName: w.sectionName || w.workoutName || null,
            week: w.week ?? null,
            workoutIndex: w.workoutIndex ?? null,
          }));
        }
      } catch {}
    }
    return [];
  }, [user, completedWorkoutsData]);
  
  // Fetch published programs from API (database programs with UUID IDs)
  const { data: apiProgramsData, isLoading: isProgramsLoading } = useQuery<{ published: any[]; drafts: any[]; userPrograms?: any[] }>({
    queryKey: ['/api/programs'],
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev, // Keep previous data during refetch to prevent flash
  });
  
  // Keep track of last known programs - use localStorage for persistence across query clears
  const getInitialPrograms = (): Program[] => {
    try {
      const cached = localStorage.getItem('myFitBeats_cachedPrograms');
      if (cached) return JSON.parse(cached);
    } catch {}
    return [];
  };
  const lastKnownProgramsRef = useRef<Program[]>(getInitialPrograms());
  
  // Convert API programs to the Program interface format
  const apiPrograms: Program[] = useMemo(() => {
    if (apiProgramsData?.published && apiProgramsData.published.length > 0) {
      const programs = apiProgramsData.published.map((p: any) => ({
        id: p.id,
        title: p.title,
        description: p.description || '',
        difficulty: p.difficulty || 'Intermediate',
        duration: p.duration || '45 min',
        totalWeeks: p.totalWeeks || 8,
        image: p.imageUrl || p.image || '',
        sections: p.sections || [],
        sessions: p.sessions,
        spotifyPlaylistUrl: p.spotifyPlaylistUrl,
        status: p.status || 'published',
        badgeStatus: p.badgeStatus,
      }));
      lastKnownProgramsRef.current = programs;
      // Persist to localStorage for recovery after query clears
      try {
        localStorage.setItem('myFitBeats_cachedPrograms', JSON.stringify(programs));
      } catch {}
      return programs;
    }
    // Keep last known data during auth refresh or query clear
    if (lastKnownProgramsRef.current.length > 0) {
      return lastKnownProgramsRef.current;
    }
    // Try localStorage fallback
    try {
      const cached = localStorage.getItem('myFitBeats_cachedPrograms');
      if (cached) {
        const programs = JSON.parse(cached);
        if (Array.isArray(programs) && programs.length > 0) {
          lastKnownProgramsRef.current = programs;
          return programs;
        }
      }
    } catch {}
    return [];
  }, [apiProgramsData]);
  
  // Convert user's private programs to the Program interface format
  const userPrivatePrograms: Program[] = useMemo(() => {
    if (!apiProgramsData?.userPrograms) return [];
    return apiProgramsData.userPrograms.map((p: any) => ({
      id: p.id,
      title: p.title,
      description: p.description || '',
      difficulty: p.difficulty || 'Intermediate',
      duration: p.duration || '45 min',
      totalWeeks: p.totalWeeks || 8,
      image: p.imageUrl || p.image || '',
      sections: p.sections || [],
      sessions: p.sessions,
      spotifyPlaylistUrl: p.spotifyPlaylistUrl,
      status: p.status || 'published',
      badgeStatus: p.badgeStatus,
      isCustom: true,
    }));
  }, [apiProgramsData]);

  const queryClient = useQueryClient();

  // Ensure CURATED_PLAYLISTS is always an array to prevent spread errors
  const safeCuratedPlaylists = CURATED_PLAYLISTS || [];
  const visibleCurated = useMemo(() => 
    safeCuratedPlaylists.filter(g => isCuratedVisible(g.value)),
    [safeCuratedPlaylists, isCuratedVisible]
  );

  const allVisiblePlaylists = useMemo(() => [
    ...visibleCurated.map(g => ({ id: g.value, name: g.label, type: 'curated' as const, url: (g as any).spotifyUrl || `https://open.spotify.com/search/${encodeURIComponent(g.label + ' workout')}/playlists` })),
    ...(customPlaylists || []).map(p => ({ id: p.id, name: p.name, type: 'custom' as const, url: p.url })),
  ], [visibleCurated, customPlaylists]);

  const selectedPlaylist = useMemo(() => {
    const found = allVisiblePlaylists.find(p => p.id === selectedPlaylistId);
    if (found) return found;
    if (allVisiblePlaylists.length > 0) return allVisiblePlaylists[0];
    return { id: 'hip-hop', name: 'Hip-Hop', type: 'curated' as const, url: 'https://open.spotify.com/search/hip-hop%20workout/playlists' };
  }, [allVisiblePlaylists, selectedPlaylistId]);
  
  const latestWeight = useMemo(() => {
    if (!weightLogs || weightLogs.length === 0) return null;
    const latest = weightLogs[weightLogs.length - 1];
    const displayWeight = weightUnit === "lbs" ? latest.weight * 2.20462 : latest.weight;
    return displayWeight.toFixed(1);
  }, [weightLogs, weightUnit]);
  
  // Program memberships - unified state
  const [memberships, setMemberships] = useState<ProgramMembership[]>(() => loadProgramMemberships());
  const lastBackendDataRef = useRef<string>("");
  
  // Fetch memberships from backend when logged in
  // Always refetch on mount to ensure fresh data after navigation
  const { data: backendUserPrograms, isLoading: isUserProgramsLoading } = useQuery<Array<{
    id: string;
    programId: string;
    status: string;
    isFavorite: boolean;
    source: string;
  }>>({
    queryKey: ['/api/user-programs'],
    enabled: !!user,
    staleTime: 0,
    refetchOnMount: 'always',
    placeholderData: (prev) => prev, // Keep previous data during refetch to prevent flash
  });
  
  // Merge backend memberships with local - re-sync whenever backend data changes
  // Backend is the source of truth
  useEffect(() => {
    if (user && backendUserPrograms) {
      // Create snapshot to detect actual data changes
      const backendSnapshot = JSON.stringify(backendUserPrograms.map(p => `${p.programId}:${p.status}:${p.isFavorite}`).sort());
      
      // Only update if backend data has actually changed
      if (backendSnapshot !== lastBackendDataRef.current) {
        lastBackendDataRef.current = backendSnapshot;
        
        const localMemberships = loadProgramMemberships();
        const localMap = new Map(localMemberships.map(m => [m.programId, m]));
        
        // Backend is source of truth - only use backend memberships
        const merged: ProgramMembership[] = [];
        
        // Add all backend memberships, preserving local addedAt if available
        for (const bp of backendUserPrograms) {
          const local = localMap.get(bp.programId);
          merged.push({
            programId: bp.programId,
            status: bp.status as ProgramStatus,
            isFavorite: bp.isFavorite,
            source: (bp.source === 'expert' || bp.source === 'custom') ? bp.source : 'expert',
            addedAt: local?.addedAt || new Date().toISOString(),
          });
        }
        
        // Update localStorage to match backend (remove locally-deleted programs)
        saveProgramMemberships(merged);
        setMemberships(merged);
      }
    } else if (!user) {
      setMemberships(loadProgramMemberships());
      lastBackendDataRef.current = "";
    }
  }, [user, backendUserPrograms]);
  
  // Sync membership functions (stable refs to avoid stale closures)
  const syncMembershipToBackend = useCallback(async (membership: ProgramMembership, programName?: string) => {
    if (!user) return;
    if (!isOnline()) {
      queueProgramJoin({
        programId: membership.programId,
        programName: programName || membership.programId,
        startDate: membership.addedAt,
        status: membership.status,
        isFavorite: membership.isFavorite,
        source: membership.source,
      });
      return;
    }
    return apiRequest('PUT', `/api/user-programs/membership/${membership.programId}`, {
      status: membership.status,
      isFavorite: membership.isFavorite,
      source: membership.source,
    });
  }, [user]);
  
  const deleteMembershipFromBackend = useCallback(async (programId: string) => {
    if (!user) return;
    if (!isOnline()) {
      queueProgramLeave(programId);
      return;
    }
    const result = await apiRequest('DELETE', `/api/user-programs/membership/${programId}`);
    // Invalidate cache so other pages see the deletion immediately
    queryClient.invalidateQueries({ queryKey: ['/api/user-programs'] });
    return result;
  }, [user, queryClient]);
  
  // Keep custom programs separate (they contain the program data itself)
  const [customPrograms, setCustomPrograms] = useState<CustomProgram[]>(() => {
    const stored = localStorage.getItem("customPrograms");
    return stored ? JSON.parse(stored) : [];
  });

  const [showDiscover, setShowDiscover] = useState(false);
  const [blockExpanded, setBlockExpanded] = useState(false);
  const [discoverSearch, setDiscoverSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [showInactiveFavorites, setShowInactiveFavorites] = useState(false);
  const [showFavorites, setShowFavorites] = useState(() => {
    const stored = localStorage.getItem("myFitBeats_showFavorites");
    return stored === "true";
  });
  const [showOtherPrograms, setShowOtherPrograms] = useState(() => {
    const stored = localStorage.getItem("myFitBeats_showOtherPrograms");
    return stored !== "false"; // Default to true if not set
  });
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Stop/Remove dialog state
  const [stopDialogProgram, setStopDialogProgram] = useState<Program | null>(null);
  const [showStopDialog, setShowStopDialog] = useState(false);
  
  // Progress metric popup state
  const [progressPopup, setProgressPopup] = useState<'week' | 'streak' | 'total' | null>(null);
  
  // Training block dialog state
  const [showTrainingBlockDialog, setShowTrainingBlockDialog] = useState(false);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  
  // Drafts
  const { drafts, removeDraft } = useDrafts();
  const [draftToDelete, setDraftToDelete] = useState<string | null>(null);
  
  // Hidden/archived completed training blocks - initialize from database first, then localStorage
  const [hiddenBlockIds, setHiddenBlockIds] = useState<string[]>(() => {
    // First check user data from database
    const dbHiddenBlocks = (user as any)?.hiddenBlockIds;
    if (Array.isArray(dbHiddenBlocks) && dbHiddenBlocks.length > 0) {
      // Sync to localStorage for offline access
      localStorage.setItem("myFitBeats_hidden_blocks", JSON.stringify(dbHiddenBlocks));
      return dbHiddenBlocks;
    }
    // Fallback to localStorage
    try {
      const stored = localStorage.getItem("myFitBeats_hidden_blocks");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  
  // Sync hidden blocks from user data when it changes
  useEffect(() => {
    const dbHiddenBlocks = (user as any)?.hiddenBlockIds;
    if (Array.isArray(dbHiddenBlocks)) {
      // Merge database and local - database is source of truth
      const localBlocks = (() => {
        try {
          const stored = localStorage.getItem("myFitBeats_hidden_blocks");
          return stored ? JSON.parse(stored) : [];
        } catch {
          return [];
        }
      })();
      const merged = Array.from(new Set([...dbHiddenBlocks, ...localBlocks]));
      if (merged.length !== hiddenBlockIds.length || !merged.every(id => hiddenBlockIds.includes(id))) {
        setHiddenBlockIds(merged);
        localStorage.setItem("myFitBeats_hidden_blocks", JSON.stringify(merged));
      }
    }
  }, [(user as any)?.hiddenBlockIds]);
  
  const hideBlock = async (blockId: string) => {
    const updated = [...hiddenBlockIds, blockId];
    setHiddenBlockIds(updated);
    localStorage.setItem("myFitBeats_hidden_blocks", JSON.stringify(updated));
    // Persist to database
    try {
      await apiRequest("PATCH", "/api/user/preferences", { hiddenBlockIds: updated });
    } catch (error) {
      console.error("Failed to save hidden blocks to database:", error);
    }
  };
  
  const unhideBlock = async (blockId: string) => {
    const updated = hiddenBlockIds.filter(id => id !== blockId);
    setHiddenBlockIds(updated);
    localStorage.setItem("myFitBeats_hidden_blocks", JSON.stringify(updated));
    // Persist to database
    try {
      await apiRequest("PATCH", "/api/user/preferences", { hiddenBlockIds: updated });
    } catch (error) {
      console.error("Failed to save hidden blocks to database:", error);
    }
  };
  
  const [showHiddenBlocks, setShowHiddenBlocks] = useState(false);
  
  // Celebrated blocks - blocks whose completion celebration has been dismissed
  // Initialize from database first, then localStorage
  const [celebratedBlockIds, setCelebratedBlockIds] = useState<string[]>(() => {
    // First check user data from database
    const dbCelebratedBlocks = (user as any)?.celebratedBlockIds;
    if (Array.isArray(dbCelebratedBlocks) && dbCelebratedBlocks.length > 0) {
      // Sync to localStorage for offline access
      localStorage.setItem("myFitBeats_celebrated_blocks", JSON.stringify(dbCelebratedBlocks));
      return dbCelebratedBlocks;
    }
    // Fallback to localStorage
    try {
      const stored = localStorage.getItem("myFitBeats_celebrated_blocks");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  
  // Sync celebrated blocks from user data when it changes
  useEffect(() => {
    const dbCelebratedBlocks = (user as any)?.celebratedBlockIds;
    if (Array.isArray(dbCelebratedBlocks)) {
      // Merge database and local - database is source of truth
      const localBlocks = (() => {
        try {
          const stored = localStorage.getItem("myFitBeats_celebrated_blocks");
          return stored ? JSON.parse(stored) : [];
        } catch {
          return [];
        }
      })();
      const merged = Array.from(new Set([...dbCelebratedBlocks, ...localBlocks]));
      if (merged.length !== celebratedBlockIds.length || !merged.every(id => celebratedBlockIds.includes(id))) {
        setCelebratedBlockIds(merged);
        localStorage.setItem("myFitBeats_celebrated_blocks", JSON.stringify(merged));
      }
    }
  }, [(user as any)?.celebratedBlockIds]);
  
  const celebrateBlock = async (blockId: string) => {
    const updated = [...celebratedBlockIds, blockId];
    setCelebratedBlockIds(updated);
    localStorage.setItem("myFitBeats_celebrated_blocks", JSON.stringify(updated));
    // Persist to database
    try {
      await apiRequest("PATCH", "/api/user/preferences", { celebratedBlockIds: updated });
    } catch (error) {
      console.error("Failed to save celebrated blocks to database:", error);
    }
  };
  
  // Hide upcoming training blocks section
  const [hideUpcomingBlocks, setHideUpcomingBlocks] = useState(() => {
    try {
      return localStorage.getItem("myFitBeats_hide_upcoming_blocks") === "true";
    } catch {
      return false;
    }
  });
  
  const toggleHideUpcomingBlocks = () => {
    const newValue = !hideUpcomingBlocks;
    setHideUpcomingBlocks(newValue);
    localStorage.setItem("myFitBeats_hide_upcoming_blocks", String(newValue));
  };
  
  // Check if an active workout is for a completed position
  const isActiveWorkoutCompleted = useCallback((workout: ActiveWorkout): boolean => {
    try {
      const progressKey = `programProgress_${workout.programId}`;
      const stored = localStorage.getItem(progressKey);
      if (stored) {
        const progress = JSON.parse(stored);
        const completionDates = progress.completionDates || [];
        return completionDates.some(
          (cd: { week: number; workoutIndex: number }) => 
            cd.week === workout.week && cd.workoutIndex === workout.workoutIndex
        );
      }
    } catch {}
    return false;
  }, []);

  // Active workout state for resume functionality
  const [activeWorkout, setActiveWorkoutState] = useState<ActiveWorkout | null>(() => {
    const workout = getActiveWorkout();
    // Clear if the workout position is already completed
    if (workout && isActiveWorkoutCompleted(workout)) {
      clearActiveWorkout();
      return null;
    }
    return workout;
  });
  const [activeWorkoutElapsed, setActiveWorkoutElapsed] = useState(0);
  const [showCancelWorkoutDialog, setShowCancelWorkoutDialog] = useState(false);
  
  // Track active workout and elapsed time
  useEffect(() => {
    const checkActiveWorkout = () => {
      const workout = getActiveWorkout();
      // Clear stale active workouts that are already completed
      if (workout && isActiveWorkoutCompleted(workout)) {
        clearActiveWorkout();
        setActiveWorkoutState(null);
        return;
      }
      setActiveWorkoutState(workout);
      if (workout && !workout.isPaused) {
        const now = Date.now();
        // Add stored elapsed time + time since last resume
        const liveDuration = Math.floor((now - workout.startedAt) / 1000);
        setActiveWorkoutElapsed(workout.elapsed + liveDuration);
      } else if (workout) {
        setActiveWorkoutElapsed(workout.elapsed);
      }
    };
    
    checkActiveWorkout();
    const interval = setInterval(checkActiveWorkout, 1000);
    return () => clearInterval(interval);
  }, [isActiveWorkoutCompleted]);
  
  const formatElapsedTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCancelWorkout = () => {
    clearActiveWorkout();
    setActiveWorkoutState(null);
    setShowCancelWorkoutDialog(false);
  };
  
  // Save memberships to localStorage whenever they change
  useEffect(() => {
    saveProgramMemberships(memberships);
  }, [memberships]);
  
  // Sync memberships to backend when they change (after initial sync is complete)
  const previousMembershipsRef = useRef<ProgramMembership[]>([]);
  const isSyncingRef = useRef(false);
  
  useEffect(() => {
    // Only sync to backend after initial merge is complete (when we've received backend data)
    if (!user || !lastBackendDataRef.current || isSyncingRef.current) return;
    
    // Skip if this is the first render after mount
    if (previousMembershipsRef.current.length === 0 && memberships.length > 0) {
      previousMembershipsRef.current = memberships;
      return;
    }
    
    const prevMap = new Map(previousMembershipsRef.current.map(m => [m.programId, m]));
    const currMap = new Map(memberships.map(m => [m.programId, m]));
    
    const toSync: ProgramMembership[] = [];
    const toDelete: string[] = [];
    
    // Find added/updated memberships
    for (const m of memberships) {
      const prev = prevMap.get(m.programId);
      if (!prev || prev.status !== m.status || prev.isFavorite !== m.isFavorite) {
        toSync.push(m);
      }
    }
    
    // Find deleted memberships
    for (const prev of previousMembershipsRef.current) {
      if (!currMap.has(prev.programId)) {
        toDelete.push(prev.programId);
      }
    }
    
    // Batch sync to avoid race conditions
    if (toSync.length > 0 || toDelete.length > 0) {
      isSyncingRef.current = true;
      Promise.all([
        ...toSync.map(m => syncMembershipToBackend(m).catch(console.error)),
        ...toDelete.map(id => deleteMembershipFromBackend(id).catch(console.error)),
      ]).finally(() => {
        isSyncingRef.current = false;
      });
    }
    
    previousMembershipsRef.current = memberships;
  }, [memberships, user, syncMembershipToBackend, deleteMembershipFromBackend]);
  
  // Derived state from memberships
  const getMembership = (programId: string) => memberships.find(m => m.programId === programId);
  const isCurrent = (programId: string) => getMembership(programId)?.status === 'current';
  const isFavorite = (programId: string) => getMembership(programId)?.isFavorite || false;
  const isArchived = (programId: string) => getMembership(programId)?.status === 'archived';

  // Refresh program data when window regains focus
  useEffect(() => {
    const handleFocus = () => setRefreshKey(k => k + 1);
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  const startTutorial = () => {
    window.location.href = "/tutorial";
  };

  // Use API programs for matching with backend memberships - keep ALL programs by ID
  // (no title deduplication here - that's only for the Browse section to avoid showing duplicates)
  const allPrograms = useMemo(() => {
    const programMap = new Map<string, Program>();
    
    // Add all API programs by ID (includes archived programs user has memberships to)
    apiPrograms.forEach(p => {
      programMap.set(p.id, p);
    });
    
    // Add user's private published programs
    userPrivatePrograms.forEach(p => {
      if (!programMap.has(p.id)) {
        programMap.set(p.id, p);
      }
    });
    
    // Add custom programs by ID
    customPrograms.forEach(p => {
      if (!programMap.has(p.id)) {
        programMap.set(p.id, p as Program);
      }
    });
    
    return Array.from(programMap.values());
  }, [apiPrograms, userPrivatePrograms, customPrograms]);
  
  // Available programs: API programs not in current or favorited memberships
  // Deduplicated by title to avoid showing archived+published duplicates in Browse
  const availablePrograms = useMemo(() => {
    const seenTitles = new Set<string>();
    return apiPrograms.filter(p => {
      // Only show published programs in Browse (not archived)
      if (p.status === 'archived') return false;
      
      const membership = getMembership(p.id);
      // Exclude only if current or still favorited
      if (membership?.status === 'current' || membership?.isFavorite) return false;
      
      // Deduplicate by title for Browse section
      if (seenTitles.has(p.title)) return false;
      seenTitles.add(p.title);
      return true;
    });
  }, [apiPrograms, memberships, getMembership]);
  
  const filteredAvailablePrograms = useMemo(() => {
    if (!discoverSearch.trim()) return availablePrograms;
    const search = discoverSearch.toLowerCase();
    return availablePrograms.filter(p => 
      p.title.toLowerCase().includes(search) || 
      p.description?.toLowerCase().includes(search) ||
      p.difficulty?.toLowerCase().includes(search)
    );
  }, [availablePrograms, discoverSearch]);

  const { data: trainingBlocks = [] } = useTrainingBlocks();
  
  const { 
    sessions: scheduledSessions, 
    deleteByProgram: deleteScheduledByProgram,
    syncFromLocal: syncScheduleFromLocal 
  } = useWorkoutSchedule();
  
  // Sync scheduled sessions from localStorage when user logs in
  useEffect(() => {
    if (user && lastBackendDataRef.current) {
      syncScheduleFromLocal().catch(console.error);
    }
  }, [user, syncScheduleFromLocal]);

  const activeBlock = useMemo(() => {
    const today = new Date();
    return trainingBlocks.find(block => {
      const start = new Date(block.startDate);
      const end = new Date(block.endDate);
      return today >= start && today <= end && block.status !== "completed";
    });
  }, [trainingBlocks]);

  const upcomingBlocks = useMemo(() => {
    const today = new Date();
    return trainingBlocks
      .filter(block => new Date(block.startDate) > today && block.status !== "completed")
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
      .slice(0, 3);
  }, [trainingBlocks]);

  const pastBlocks = useMemo(() => {
    const today = new Date();
    return trainingBlocks
      .filter(block => (new Date(block.endDate) < today || block.status === "completed") && !hiddenBlockIds.includes(block.id))
      .sort((a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime())
      .slice(0, 3);
  }, [trainingBlocks, hiddenBlockIds]);
  
  const hiddenBlocks = useMemo(() => {
    const today = new Date();
    return trainingBlocks
      .filter(block => (new Date(block.endDate) < today || block.status === "completed") && hiddenBlockIds.includes(block.id))
      .sort((a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime());
  }, [trainingBlocks, hiddenBlockIds]);
  
  // Recently completed block - show celebration if not yet dismissed
  const recentlyCompletedBlock = useMemo(() => {
    const today = new Date();
    // Find blocks that ended within the last 7 days and haven't been celebrated
    return trainingBlocks.find(block => {
      const endDate = new Date(block.endDate);
      const daysSinceEnd = differenceInDays(today, endDate);
      // Block ended (end date passed) but within last 7 days, and not yet celebrated
      return endDate < today && daysSinceEnd <= 7 && !celebratedBlockIds.includes(block.id);
    });
  }, [trainingBlocks, celebratedBlockIds]);
  
  // Stats for the recently completed block
  const completedBlockStats = useMemo(() => {
    if (!recentlyCompletedBlock) return null;
    
    const startDate = new Date(recentlyCompletedBlock.startDate);
    const endDate = new Date(recentlyCompletedBlock.endDate);
    const totalDays = differenceInDays(endDate, startDate);
    const totalWeeks = Math.round(totalDays / 7);
    const programCount = recentlyCompletedBlock.programIds?.length || 0;
    
    // Use database data instead of localStorage
    const workoutsCompleted = dbCompletedWorkouts.filter((w) => {
      const workoutDate = new Date(w.date);
      return workoutDate >= startDate && workoutDate <= endDate;
    }).length;
    
    return { totalDays, totalWeeks, programCount, workoutsCompleted };
  }, [recentlyCompletedBlock, dbCompletedWorkouts]);

  const blockPrograms = useMemo(() => {
    if (!activeBlock?.programIds) return [];
    // Only show programs that are in the training block AND the user still has an active membership to
    const activeMembershipIds = memberships
      .filter(m => m.status === 'current' || m.status === 'favorite')
      .map(m => m.programId);
    return allPrograms.filter(p => 
      activeBlock.programIds?.includes(p.id) && activeMembershipIds.includes(p.id)
    );
  }, [activeBlock, allPrograms, memberships]);

  const blockProgress = useMemo(() => {
    if (!activeBlock) return { workoutsCompleted: 0, totalDays: 0, daysElapsed: 0, percentComplete: 0 };
    
    const startDate = new Date(activeBlock.startDate);
    const endDate = new Date(activeBlock.endDate);
    const today = new Date();
    
    const totalDays = Math.max(1, differenceInDays(endDate, startDate));
    const daysElapsed = Math.max(1, Math.min(totalDays, differenceInDays(today, startDate) + 1));
    const percentComplete = Math.round((daysElapsed / totalDays) * 100);
    
    // Use database data instead of localStorage
    const workoutsCompleted = dbCompletedWorkouts.filter((w) => {
      const workoutDate = new Date(w.date);
      return workoutDate >= startDate && workoutDate <= today;
    }).length;
    
    return { workoutsCompleted, totalDays, daysElapsed, percentComplete };
  }, [activeBlock, dbCompletedWorkouts]);

  const blockCategoryColors = useMemo(() => {
    const category = activeBlock?.category as TrainingBlockCategory || "general";
    const colorMap: Record<TrainingBlockCategory, { border: string; bgLight: string; bgMedium: string; text: string; textLight: string; progressBg: string; progressBar: string }> = {
      general: { border: "border-sky-500/40", bgLight: "bg-gradient-to-br from-sky-500/10 to-sky-600/5", bgMedium: "bg-sky-500/20", text: "text-sky-600 dark:text-sky-400", textLight: "text-sky-600 dark:text-sky-300", progressBg: "bg-sky-200/50 dark:bg-sky-950/50", progressBar: "bg-sky-500" },
      hypertrophy: { border: "border-orange-500/40", bgLight: "bg-gradient-to-br from-orange-500/10 to-orange-600/5", bgMedium: "bg-orange-500/20", text: "text-orange-600 dark:text-orange-400", textLight: "text-orange-600 dark:text-orange-300", progressBg: "bg-orange-200/50 dark:bg-orange-950/50", progressBar: "bg-orange-500" },
      strength: { border: "border-violet-500/40", bgLight: "bg-gradient-to-br from-violet-500/10 to-violet-600/5", bgMedium: "bg-violet-500/20", text: "text-violet-600 dark:text-violet-400", textLight: "text-violet-600 dark:text-violet-300", progressBg: "bg-violet-200/50 dark:bg-violet-950/50", progressBar: "bg-violet-500" },
      conditioning: { border: "border-emerald-500/40", bgLight: "bg-gradient-to-br from-emerald-500/10 to-emerald-600/5", bgMedium: "bg-emerald-500/20", text: "text-emerald-600 dark:text-emerald-400", textLight: "text-emerald-600 dark:text-emerald-300", progressBg: "bg-emerald-200/50 dark:bg-emerald-950/50", progressBar: "bg-emerald-500" },
      athletic: { border: "border-cyan-500/40", bgLight: "bg-gradient-to-br from-cyan-500/10 to-cyan-600/5", bgMedium: "bg-cyan-500/20", text: "text-cyan-600 dark:text-cyan-400", textLight: "text-cyan-600 dark:text-cyan-300", progressBg: "bg-cyan-200/50 dark:bg-cyan-950/50", progressBar: "bg-cyan-500" },
      longevity: { border: "border-rose-500/40", bgLight: "bg-gradient-to-br from-rose-500/10 to-rose-600/5", bgMedium: "bg-rose-500/20", text: "text-rose-600 dark:text-rose-400", textLight: "text-rose-600 dark:text-rose-300", progressBg: "bg-rose-200/50 dark:bg-rose-950/50", progressBar: "bg-rose-500" },
      rest_recovery: { border: "border-teal-500/40", bgLight: "bg-gradient-to-br from-teal-500/10 to-teal-600/5", bgMedium: "bg-teal-500/20", text: "text-teal-600 dark:text-teal-400", textLight: "text-teal-600 dark:text-teal-300", progressBg: "bg-teal-200/50 dark:bg-teal-950/50", progressBar: "bg-teal-500" },
      weight_loss: { border: "border-amber-500/40", bgLight: "bg-gradient-to-br from-amber-500/10 to-amber-600/5", bgMedium: "bg-amber-500/20", text: "text-amber-600 dark:text-amber-400", textLight: "text-amber-600 dark:text-amber-300", progressBg: "bg-amber-200/50 dark:bg-amber-950/50", progressBar: "bg-amber-500" },
      maintenance: { border: "border-slate-500/40", bgLight: "bg-gradient-to-br from-slate-500/10 to-slate-600/5", bgMedium: "bg-slate-500/20", text: "text-slate-600 dark:text-slate-400", textLight: "text-slate-600 dark:text-slate-300", progressBg: "bg-slate-200/50 dark:bg-slate-950/50", progressBar: "bg-slate-500" },
      technique: { border: "border-fuchsia-500/40", bgLight: "bg-gradient-to-br from-fuchsia-500/10 to-fuchsia-600/5", bgMedium: "bg-fuchsia-500/20", text: "text-fuchsia-600 dark:text-fuchsia-400", textLight: "text-fuchsia-600 dark:text-fuchsia-300", progressBg: "bg-fuchsia-200/50 dark:bg-fuchsia-950/50", progressBar: "bg-fuchsia-500" },
      other: { border: "border-indigo-500/40", bgLight: "bg-gradient-to-br from-indigo-500/10 to-indigo-600/5", bgMedium: "bg-indigo-500/20", text: "text-indigo-600 dark:text-indigo-400", textLight: "text-indigo-600 dark:text-indigo-300", progressBg: "bg-indigo-200/50 dark:bg-indigo-950/50", progressBar: "bg-indigo-500" },
    };
    return colorMap[category] || colorMap.general;
  }, [activeBlock?.category]);

  // Helper to parse date string as local date (handles "YYYY-MM-DD" format)
  const parseLocalDate = (dateStr: string): Date => {
    // If the date string is just YYYY-MM-DD, parse it as local time
    // by splitting and creating a date with explicit local components
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    // Otherwise, parse as-is (will handle ISO strings with time)
    return new Date(dateStr);
  };

  // Progress metrics - using database data instead of localStorage
  const progressMetrics = useMemo(() => {
    let totalWorkouts = dbCompletedWorkouts.length;
    let thisWeekWorkouts = 0;
    let activeWeeksStreak = 0;
    let lastWorkoutDaysAgo = 0;
    
    thisWeekWorkouts = dbCompletedWorkouts.filter((w) => {
      const date = parseLocalDate(w.date);
      return isThisWeek(date, { weekStartsOn: 1 });
    }).length;

    // Calculate consecutive active weeks streak (Monday-Sunday weeks)
    // A week is active if it has at least 1 workout
    // Streak breaks if any week is missed
    if (dbCompletedWorkouts.length > 0) {
      // Build a set of week start dates that have workouts
      // Use date-only format (yyyy-MM-dd) to avoid DST issues
      const weeksWithWorkouts = new Set<string>();
      let mostRecentDate: Date | null = null;
      
      dbCompletedWorkouts.forEach((w) => {
        const date = parseLocalDate(w.date);
        const weekStart = startOfWeek(date, { weekStartsOn: 1 });
        weeksWithWorkouts.add(format(weekStart, 'yyyy-MM-dd'));
        if (!mostRecentDate || date > mostRecentDate) {
          mostRecentDate = date;
        }
      });
      
      if (mostRecentDate) {
        lastWorkoutDaysAgo = differenceInDays(new Date(), mostRecentDate);
      }
      
      // Calculate streak starting from current week going backwards
      const today = new Date();
      let currentWeekStart = startOfWeek(today, { weekStartsOn: 1 });
      
      // Check each week going backwards using subWeeks for DST stability
      while (weeksWithWorkouts.has(format(currentWeekStart, 'yyyy-MM-dd'))) {
        activeWeeksStreak++;
        currentWeekStart = subWeeks(currentWeekStart, 1);
      }
    }

    return { totalWorkouts, thisWeekWorkouts, activeWeeks: activeWeeksStreak, lastWorkoutDaysAgo };
  }, [dbCompletedWorkouts]);

  // Helper to find next incomplete workout for a program
  // Uses both localStorage completionDates AND database completed workouts
  const findNextIncompleteForProgram = (program: Program): { week: number; workoutIndex: number } => {
    let completionDates: { week: number; workoutIndex: number }[] = [];
    
    try {
      const progressKey = `programProgress_${program.id}`;
      const stored = localStorage.getItem(progressKey);
      if (stored) {
        const progress = JSON.parse(stored);
        completionDates = progress.completionDates || [];
      }
    } catch {}
    
    // Also check database completed workouts for this program
    const dbCompletions = dbCompletedWorkouts
      .filter(w => w.programId === program.id && w.week != null && w.workoutIndex != null)
      .map(w => ({ week: w.week!, workoutIndex: w.workoutIndex! }));
    
    // Merge both sources (avoid duplicates by checking existence)
    const allCompletions = [...completionDates];
    for (const dbc of dbCompletions) {
      const exists = allCompletions.some(
        c => c.week === dbc.week && c.workoutIndex === dbc.workoutIndex
      );
      if (!exists) {
        allCompletions.push(dbc);
      }
    }
    
    const totalWeeks = program.totalWeeks || 8;
    
    // Track the last defined workout position (in case all are complete)
    let lastDefinedPosition: { week: number; workoutIndex: number } | null = null;
    
    // Scan through all workouts to find the first incomplete one
    for (let week = 1; week <= totalWeeks; week++) {
      const weekWorkouts = getWeekWorkouts(program, week);
      for (let wIdx = 0; wIdx < weekWorkouts.length; wIdx++) {
        // Track this as last defined position
        lastDefinedPosition = { week, workoutIndex: wIdx };
        
        const isCompleted = allCompletions.some(
          cd => cd.week === week && cd.workoutIndex === wIdx
        );
        if (!isCompleted) {
          return { week, workoutIndex: wIdx };
        }
      }
    }
    
    // All defined workouts are complete - return last defined position
    // This allows user to navigate backwards through completed sessions
    if (lastDefinedPosition) {
      return lastDefinedPosition;
    }
    
    // Fallback if program has no workouts defined
    return { week: 1, workoutIndex: 0 };
  };

  const getProgramProgress = (program: Program) => {
    let currentWeek = 1;
    let currentWorkout = 0;
    let lastActivity: string | null = null;
    let completionDates: { week: number; workoutIndex: number }[] = [];
    
    try {
      const progressKey = `programProgress_${program.id}`;
      const stored = localStorage.getItem(progressKey);
      if (stored) {
        const progress = JSON.parse(stored);
        currentWeek = progress.currentWeek || 1;
        currentWorkout = progress.currentWorkout || 0;
        lastActivity = progress.lastActivity || null;
        completionDates = progress.completionDates || [];
      }
    } catch {}
    
    // Always find the first incomplete workout (ignore saved position)
    const next = findNextIncompleteForProgram(program);
    currentWeek = next.week;
    currentWorkout = next.workoutIndex;
    
    const weekWorkouts = getWeekWorkouts(program, currentWeek);
    return { currentWeek, currentWorkout, weekWorkouts, lastActivity };
  };

  const getNextWorkout = (program: Program) => {
    const { currentWeek, currentWorkout, weekWorkouts } = getProgramProgress(program);
    const currentSession = weekWorkouts[currentWorkout]?.session;
    // Show session name or generic "Session X" (not "Week X" which implies calendar time)
    return currentSession?.name || `Workout ${String.fromCharCode(65 + currentWorkout)}`;
  };

  const isProgramActive = (program: Program) => {
    const { lastActivity } = getProgramProgress(program);
    if (!lastActivity) return true;
    const daysSinceActivity = differenceInDays(new Date(), new Date(lastActivity));
    return daysSinceActivity <= 7;
  };

  // Categorize programs based on new membership model
  // Current Programs: status === 'current' (can also be favorite - shown with heart icon)
  // Favorites: ALL programs with isFavorite === true (may overlap with current)
  const { currentPrograms, inactiveCurrentPrograms, favoritePrograms, inactiveFavorites, archivedPrograms } = useMemo(() => {
    const current: Program[] = [];
    const inactiveCurrent: Program[] = [];
    const favorites: Program[] = [];
    const inactiveFavs: Program[] = [];
    const archived: Program[] = [];
    
    for (const membership of memberships) {
      const program = allPrograms.find(p => p.id === membership.programId);
      if (!program) continue;
      
      if (membership.status === 'archived') {
        archived.push(program);
        continue;
      }
      
      const isActive = isProgramActive(program);
      
      // Current programs: status === 'current'
      if (membership.status === 'current') {
        if (isActive) {
          current.push(program);
        } else {
          inactiveCurrent.push(program);
        }
      }
      
      // Favorites section: programs with isFavorite === true OR status === 'favorite' (any status except archived)
      if (membership.isFavorite || membership.status === 'favorite') {
        if (isActive) {
          favorites.push(program);
        } else {
          inactiveFavs.push(program);
        }
      }
    }
    
    return { 
      currentPrograms: current, 
      inactiveCurrentPrograms: inactiveCurrent, 
      favoritePrograms: favorites, 
      inactiveFavorites: inactiveFavs,
      archivedPrograms: archived
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberships, allPrograms, refreshKey]);

  // Detect the next workout across ALL active programs (for primary CTA)
  // Priority: 1) Next scheduled workout from calendar, 2) Most recently active program
  // Note: Quick session programs (programType='quick_session') are excluded from UP NEXT
  const nextWorkoutInfo = useMemo(() => {
    // Get all current programs (active ones first priority), excluding quick sessions
    const allCurrentProgramsList = [...(currentPrograms || []), ...(inactiveCurrentPrograms || [])]
      .filter((p: Program) => p.programType !== 'quick_session');
    
    if (allCurrentProgramsList.length === 0) return null;
    
    // Check for next scheduled workout from today onwards
    // Exclude scheduled sessions that have already been completed
    const today = format(new Date(), 'yyyy-MM-dd');
    const upcomingScheduled = scheduledSessions
      .filter(s => {
        if (s.scheduledDate < today) return false;
        // Check if this scheduled session was already completed
        const isCompleted = dbCompletedWorkouts.some(w =>
          w.programId === s.programId &&
          w.workoutIndex === s.workoutIndex &&
          w.date === s.scheduledDate
        );
        return !isCompleted;
      })
      .sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate) || a.workoutIndex - b.workoutIndex);
    
    if (upcomingScheduled.length > 0) {
      const nextScheduled = upcomingScheduled[0];
      const scheduledProgram = allPrograms.find(p => p.id === nextScheduled.programId);
      
      if (scheduledProgram) {
        const isToday = nextScheduled.scheduledDate === today;
        const scheduledDate = new Date(nextScheduled.scheduledDate + 'T00:00:00');
        const dateLabel = isToday ? 'Today' : format(scheduledDate, 'EEE, MMM d');
        
        return {
          program: scheduledProgram,
          session: null,
          workoutName: nextScheduled.workoutName,
          workoutIndex: nextScheduled.workoutIndex,
          currentWeek: nextScheduled.week,
          totalPrograms: allCurrentProgramsList.length,
          lastActivity: null,
          isScheduled: true,
          scheduledDate: nextScheduled.scheduledDate,
          scheduledDateLabel: dateLabel,
        };
      }
    }
    
    // Fall back to most recently active program
    let primaryProgram = allCurrentProgramsList[0];
    let mostRecentActivity: Date | null = null;
    
    for (const program of allCurrentProgramsList) {
      const { lastActivity } = getProgramProgress(program);
      if (lastActivity) {
        const activityDate = new Date(lastActivity);
        if (!mostRecentActivity || activityDate > mostRecentActivity) {
          mostRecentActivity = activityDate;
          primaryProgram = program;
        }
      }
    }
    
    const progress = getProgramProgress(primaryProgram);
    const nextSession = progress.weekWorkouts[progress.currentWorkout]?.session;
    
    return {
      program: primaryProgram,
      session: nextSession,
      workoutName: nextSession?.name || primaryProgram.title,
      workoutIndex: progress.currentWorkout,
      currentWeek: progress.currentWeek,
      totalPrograms: allCurrentProgramsList.length,
      lastActivity: mostRecentActivity,
      isScheduled: false,
    };
  }, [currentPrograms, inactiveCurrentPrograms, scheduledSessions, allPrograms, dbCompletedWorkouts]);

  // Detect today's completed workout for the primary program (for "View Today's Session" CTA)
  // Note: parseLocalDate helper is defined below but needed here - move its definition up
  const todayCompletedWorkout = useMemo(() => {
    if (!nextWorkoutInfo?.program?.id) return null;
    
    const today = new Date();
    const todayCompletion = dbCompletedWorkouts.find(w => {
      if (w.programId !== nextWorkoutInfo.program.id) return false;
      
      try {
        // Use same date parsing logic as parseLocalDate:
        // For YYYY-MM-DD strings, parse as local date to avoid timezone issues
        // For ISO strings with T, parseISO handles correctly
        let completionDate: Date;
        if (/^\d{4}-\d{2}-\d{2}$/.test(w.date)) {
          const [year, month, day] = w.date.split('-').map(Number);
          completionDate = new Date(year, month - 1, day);
        } else {
          completionDate = parseISO(w.date);
        }
        return isSameDay(completionDate, today);
      } catch {
        return false;
      }
    });
    
    if (todayCompletion && todayCompletion.week !== null && todayCompletion.workoutIndex !== null) {
      return {
        ...todayCompletion,
        week: todayCompletion.week!,
        workoutIndex: todayCompletion.workoutIndex!,
      };
    }
    return null;
  }, [nextWorkoutInfo?.program?.id, dbCompletedWorkouts]);

  // Start a program (adds to current programs)
  const startProgram = (programId: string) => {
    const isCustom = customPrograms.some(p => p.id === programId);
    const existing = getMembership(programId);
    
    const newMembership: ProgramMembership = {
      programId,
      status: 'current' as ProgramStatus,
      isFavorite: existing?.isFavorite || false,
      source: isCustom ? 'custom' : 'expert',
      addedAt: existing?.addedAt || new Date().toISOString()
    };
    
    if (existing) {
      // Update existing membership to current status
      setMemberships(prev => prev.map(m => 
        m.programId === programId ? newMembership : m
      ));
    } else {
      // Create new membership
      setMemberships(prev => [...prev, newMembership]);
    }
  };

  // Add to favorites only (without starting)
  const favoriteProgram = (programId: string) => {
    const isCustom = customPrograms.some(p => p.id === programId);
    const existing = getMembership(programId);
    
    if (existing) {
      // If it's a favorite-only program and we're unfavoriting, remove it entirely
      if (existing.status === 'favorite' && existing.isFavorite) {
        // Unfavoriting a favorite-only program - remove membership (returns to Browse)
        setMemberships(prev => prev.filter(m => m.programId !== programId));
      } else {
        // Toggle favorite for current programs
        setMemberships(prev => prev.map(m => 
          m.programId === programId ? { ...m, isFavorite: !m.isFavorite } : m
        ));
      }
    } else {
      // Create new membership as favorite-only
      setMemberships(prev => [...prev, {
        programId,
        status: 'favorite' as ProgramStatus,
        isFavorite: true,
        source: isCustom ? 'custom' : 'expert',
        addedAt: new Date().toISOString()
      }]);
    }
  };

  // Toggle favorite status for existing programs
  const toggleFavorite = (programId: string) => {
    const existing = getMembership(programId);
    if (existing) {
      // If it's a favorite-only program and we're unfavoriting, remove it entirely
      if (existing.status === 'favorite' && existing.isFavorite) {
        setMemberships(prev => prev.filter(m => m.programId !== programId));
      } else {
        setMemberships(prev => prev.map(m => 
          m.programId === programId ? { ...m, isFavorite: !m.isFavorite } : m
        ));
      }
    }
  };

  // Get scheduled sessions for a program (now using server sync)
  const getScheduledSessionsForProgram = (programId: string) => {
    const today = new Date().toISOString().split('T')[0];
    return scheduledSessions.filter(s => s.programId === programId && s.scheduledDate >= today);
  };

  // Check if program is in any training block
  const getTrainingBlocksForProgram = (programId: string) => {
    return trainingBlocks.filter(block => block.programIds?.includes(programId));
  };

  // Open stop dialog for a program
  const openStopDialog = (program: Program) => {
    setStopDialogProgram(program);
    setShowStopDialog(true);
  };

  // Handle stop program actions
  const handleStopProgram = (action: 'keep_favorite' | 'remove' | 'archive', removeFromBlocks: boolean = false) => {
    if (!stopDialogProgram) return;
    
    const programId = stopDialogProgram.id;
    const isCustom = (stopDialogProgram as CustomProgram).isCustom;
    const hasModifications = !isCustom && detectProgramModifications(programId);
    
    // Remove scheduled sessions if removing completely (using server sync)
    if (action === 'remove' || action === 'archive') {
      deleteScheduledByProgram(programId).catch(console.error);
    }
    
    switch (action) {
      case 'keep_favorite':
        // Set status to favorite, keep isFavorite true
        setMemberships(prev => prev.map(m => 
          m.programId === programId 
            ? { ...m, status: 'favorite' as ProgramStatus, isFavorite: true } 
            : m
        ));
        break;
        
      case 'remove':
        if (isCustom) {
          // Custom programs get archived
          setMemberships(prev => prev.map(m => 
            m.programId === programId 
              ? { ...m, status: 'archived' as ProgramStatus, isFavorite: false } 
              : m
          ));
        } else if (hasModifications) {
          // Expert program with modifications: create a copy and archive it
          const newId = `${programId}_copy_${Date.now()}`;
          const copiedProgram: CustomProgram = {
            ...stopDialogProgram,
            id: newId,
            title: `${stopDialogProgram.title} (Modified)`,
            isCustom: true,
            createdBy: userId,
            experienceLevel: stopDialogProgram.experienceLevel ?? undefined,
            muscleGroups: stopDialogProgram.muscleGroups ?? undefined,
          };
          
          // Save the copied program using functional updater
          setCustomPrograms(prev => {
            const updated = [...prev, copiedProgram];
            localStorage.setItem("customPrograms", JSON.stringify(updated));
            return updated;
          });
          
          // Copy progress data to new program
          try {
            const progressKey = `programProgress_${programId}`;
            const progress = localStorage.getItem(progressKey);
            if (progress) {
              localStorage.setItem(`programProgress_${newId}`, progress);
            }
          } catch {}
          
          // Add archived membership for the copy
          setMemberships(prev => [
            ...prev.filter(m => m.programId !== programId),
            {
              programId: newId,
              status: 'archived' as ProgramStatus,
              isFavorite: false,
              source: 'custom',
              addedAt: new Date().toISOString()
            }
          ]);
          
          // Clear progress for the original expert program
          localStorage.removeItem(`programProgress_${programId}`);
        } else {
          // Unmodified expert programs: remove membership entirely (returns to Available)
          setMemberships(prev => prev.filter(m => m.programId !== programId));
          // Clear progress data
          localStorage.removeItem(`programProgress_${programId}`);
        }
        break;
        
      case 'archive':
        if (!isCustom && hasModifications) {
          // Expert program with modifications: create a copy and archive it, freeing up the original
          const newId = `${programId}_copy_${Date.now()}`;
          const copiedProgram: CustomProgram = {
            ...stopDialogProgram,
            id: newId,
            title: `${stopDialogProgram.title} (Modified)`,
            isCustom: true,
            createdBy: userId,
            experienceLevel: stopDialogProgram.experienceLevel ?? undefined,
            muscleGroups: stopDialogProgram.muscleGroups ?? undefined,
          };
          
          // Save the copied program
          setCustomPrograms(prev => {
            const updated = [...prev, copiedProgram];
            localStorage.setItem("customPrograms", JSON.stringify(updated));
            return updated;
          });
          
          // Copy progress data to new program
          try {
            const progressKey = `programProgress_${programId}`;
            const progress = localStorage.getItem(progressKey);
            if (progress) {
              localStorage.setItem(`programProgress_${newId}`, progress);
            }
          } catch {}
          
          // Remove original membership and add archived one for the copy
          setMemberships(prev => [
            ...prev.filter(m => m.programId !== programId),
            {
              programId: newId,
              status: 'archived' as ProgramStatus,
              isFavorite: false,
              source: 'custom',
              addedAt: new Date().toISOString()
            }
          ]);
          
          // Clear progress for the original expert program
          localStorage.removeItem(`programProgress_${programId}`);
        } else {
          // Custom programs or unmodified expert programs: just archive
          setMemberships(prev => prev.map(m => 
            m.programId === programId 
              ? { ...m, status: 'archived' as ProgramStatus, isFavorite: false } 
              : m
          ));
        }
        break;
    }
    
    setShowStopDialog(false);
    setStopDialogProgram(null);
  };

  // Restore archived program
  const restoreProgram = (programId: string) => {
    setMemberships(prev => prev.map(m => 
      m.programId === programId 
        ? { ...m, status: 'current' as ProgramStatus } 
        : m
    ));
  };

  // Permanently delete a custom program
  const deleteProgram = (programId: string) => {
    const program = customPrograms.find(p => p.id === programId);
    if (program && program.createdBy === userId) {
      setCustomPrograms(prev => {
        const updated = prev.filter(p => p.id !== programId);
        localStorage.setItem("customPrograms", JSON.stringify(updated));
        return updated;
      });
      setMemberships(prev => prev.filter(m => m.programId !== programId));
    }
  };

  const canDelete = (program: Program) => {
    return (program as CustomProgram).isCustom && (program as CustomProgram).createdBy === userId;
  };
  
  const isCustomProgram = (program: Program) => {
    return (program as CustomProgram).isCustom;
  };

  // Show loading screen until critical data is ready
  if (isProgramsLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <div className="font-outfit text-3xl font-bold text-primary animate-pulse">myFitBeats</div>
        <div className="mt-6 w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <LayoutWithSidebar isAdmin={isAdmin} isCoach={isCoach} showNotifications={true}>
    <div className="h-screen bg-background flex flex-col overflow-hidden">
      {/* Header - Mobile only */}
      <header className="shrink-0 z-50 bg-background/95 backdrop-blur-md border-b border-border/40 md:hidden">
        <div className="container mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Flame className="w-5 h-5 text-primary" />
            <h1 className="text-lg font-bold text-foreground">myFit<span className="text-primary">Beats</span></h1>
          </div>

          <MobileNav isAdmin={isAdmin} isCoach={isCoach} />
        </div>
      </header>

      <InstallPromptBanner />

      <main className="container mx-auto px-4 py-2 md:py-1 flex-1 overflow-y-auto flex flex-col space-y-3 md:space-y-1.5">
        {/* STATS SECTION - Progress at the top */}
        <section className="shrink-0">
          <div className="flex items-center justify-center gap-4 md:gap-8 bg-card/30 rounded-lg px-4 py-1.5 border border-border/30">
            <button 
              onClick={() => setProgressPopup('week')}
              className="flex items-center gap-1 group"
              data-testid="button-progress-week"
            >
              <Zap className="w-3.5 h-3.5 text-primary" />
              <span className="text-sm font-bold text-foreground">{progressMetrics.thisWeekWorkouts}</span>
              <span className="text-xs text-muted-foreground group-hover:text-primary/80">this week</span>
            </button>
            <div className="w-px h-4 bg-border/50" />
            <button 
              onClick={() => setProgressPopup('streak')}
              className="flex items-center gap-1 group"
              data-testid="button-progress-active-weeks"
            >
              <Flame className="w-3.5 h-3.5 text-orange-400" />
              <span className="text-sm font-bold text-foreground">{progressMetrics.activeWeeks}</span>
              <span className="text-xs text-muted-foreground group-hover:text-orange-400/80">{progressMetrics.activeWeeks === 1 ? "wk" : "wks"}</span>
            </button>
            <div className="w-px h-4 bg-border/50" />
            <button 
              onClick={() => setProgressPopup('total')}
              className="flex items-center gap-1 group"
              data-testid="button-progress-total"
            >
              <Award className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-sm font-bold text-foreground">{progressMetrics.totalWorkouts}</span>
              <span className="text-xs text-muted-foreground group-hover:text-emerald-400/80">total</span>
            </button>
            <div className="w-px h-4 bg-border/50" />
            <Link href="/progress?tab=weight" className="flex items-center gap-1 group">
              <Scale className="w-3.5 h-3.5 text-violet-400" />
              <span className="text-sm font-bold text-foreground">{latestWeight || "--"}</span>
              <span className="text-xs text-muted-foreground group-hover:text-violet-400/80">{weightUnit}</span>
            </Link>
          </div>
        </section>

        {/* TRAINING BLOCKS - Stacked layout without horizontal scroll */}
        {(activeBlock || upcomingBlocks.length > 0) && (
          <section className="shrink-0 space-y-2">
            {/* Active Block Card - Full width */}
            {activeBlock && (
              <Link href={`/training-block/${activeBlock.id}`}>
                <div 
                  className={cn(
                    "rounded-xl border px-3 py-3 cursor-pointer transition-all hover:shadow-md active:scale-[0.99]",
                    blockCategoryColors.border,
                    blockCategoryColors.bgLight
                  )}
                  data-testid={`card-training-block-${activeBlock.id}`}
                >
                  <div className="flex items-center gap-2.5">
                    <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center shrink-0", blockCategoryColors.bgMedium)}>
                      <span className={blockCategoryColors.text}>
                        {CATEGORY_ICONS[activeBlock.category as TrainingBlockCategory] || <Target className="w-4 h-4" />}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h4 className="text-sm font-semibold text-foreground truncate">{activeBlock.name}</h4>
                        <span className={cn("text-xs font-medium px-2 py-0.5 rounded-full shrink-0", blockCategoryColors.bgMedium, blockCategoryColors.text)}>
                          Week {Math.max(1, Math.ceil(blockProgress.daysElapsed / 7))}/{Math.max(1, Math.round(blockProgress.totalDays / 7))}
                        </span>
                      </div>
                      <div className={cn("h-1 rounded-full overflow-hidden mt-1.5", blockCategoryColors.progressBg)}>
                        <div 
                          className={cn("h-full rounded-full transition-all", blockCategoryColors.progressBar)}
                          style={{ width: `${Math.max(3, blockProgress.percentComplete)}%` }}
                        />
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground/40 shrink-0" />
                  </div>
                </div>
              </Link>
            )}

            {/* Upcoming blocks row - compact pills */}
            {upcomingBlocks.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground">Upcoming:</span>
                {upcomingBlocks.slice(0, 3).map((block) => {
                  const blockCategory = block.category as TrainingBlockCategory;
                  const colors = CATEGORY_COLORS[blockCategory] || CATEGORY_COLORS.general;
                  return (
                    <Link key={block.id} href={`/training-block/${block.id}`}>
                      <div 
                        className="flex items-center gap-1.5 rounded-full border border-border/30 bg-card/30 px-2.5 py-1 cursor-pointer hover:bg-card/50 transition-colors"
                        data-testid={`card-upcoming-block-${block.id}`}
                      >
                        <span className={cn("w-4 h-4 rounded flex items-center justify-center shrink-0 text-xs", colors)}>
                          {CATEGORY_ICONS[blockCategory] || <Target className="w-2.5 h-2.5" />}
                        </span>
                        <span className="text-xs text-foreground">{block.name}</span>
                        <span className="text-[10px] text-muted-foreground">
                          {format(new Date(block.startDate), "MMM d")}
                        </span>
                      </div>
                    </Link>
                  );
                })}
                {upcomingBlocks.length > 3 && (
                  <Link href="/training-blocks">
                    <span className="text-xs text-muted-foreground hover:text-foreground cursor-pointer">
                      +{upcomingBlocks.length - 3} more
                    </span>
                  </Link>
                )}
                <button
                  onClick={() => setShowTrainingBlockDialog(true)}
                  className="flex items-center gap-1 rounded-full border border-dashed border-border/30 px-2 py-1 hover:bg-card/30 transition-colors"
                  data-testid="button-add-training-block"
                >
                  <Plus className="w-3 h-3 text-muted-foreground/60" />
                  <span className="text-xs text-muted-foreground/60">Add</span>
                </button>
              </div>
            )}

            {/* Show add button when no upcoming blocks but has active block */}
            {upcomingBlocks.length === 0 && activeBlock && (
              <button
                onClick={() => setShowTrainingBlockDialog(true)}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                data-testid="button-add-training-block"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Add training block</span>
              </button>
            )}
          </section>
        )}

        {/* PRIMARY: Continue Workout CTA - Shows Resume if workout in progress */}
        {/* When there's a completed workout today, show "View Today's Session" as primary action */}
        {(activeWorkout || nextWorkoutInfo) && (
          <motion.section
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="shrink-0 space-y-2"
          >
            {/* Today's Completed Workout - Primary when available */}
            {!activeWorkout && todayCompletedWorkout && (
              <button 
                onClick={() => {
                  const today = format(new Date(), 'yyyy-MM-dd');
                  let url = `/program/${nextWorkoutInfo!.program.id}?week=${todayCompletedWorkout.week}&workout=${todayCompletedWorkout.workoutIndex}&date=${today}`;
                  if (todayCompletedWorkout.instanceId) {
                    url += `&instanceId=${todayCompletedWorkout.instanceId}`;
                  } else if (todayCompletedWorkout.sessionId) {
                    url += `&sessionId=${todayCompletedWorkout.sessionId}`;
                  }
                  navigate(url);
                }}
                className="relative overflow-hidden rounded-2xl px-4 py-3 hover:shadow-lg transition-all group cursor-pointer bg-gradient-to-r from-emerald-500 to-teal-500 dark:from-emerald-600 dark:to-teal-500 hover:shadow-emerald-500/25 shadow-md w-full text-left"
                data-testid="button-view-today-workout"
                aria-label={`View completed workout: ${nextWorkoutInfo!.program.title}${(nextWorkoutInfo!.program.sessions?.length || 0) > 1 && todayCompletedWorkout.sectionName ? `, ${todayCompletedWorkout.sectionName}` : ''}`}
              >
                <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                
                <div className="flex items-center gap-4 relative">
                  <div className="w-16 h-16 md:w-18 md:h-18 rounded-full bg-white/50 dark:bg-white/40 backdrop-blur-sm flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform shadow-xl ring-2 ring-white/30" aria-hidden="true">
                    <CheckCircle className="w-8 h-8 md:w-9 md:h-9 text-white" />
                  </div>
                  
                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                    <p className="text-xs uppercase tracking-wider text-white/90 leading-none mb-1">
                      Completed Today
                    </p>
                    <h2 className="text-base font-bold text-white truncate leading-tight">
                      {nextWorkoutInfo!.program.title}
                    </h2>
                    {(nextWorkoutInfo!.program.sessions?.length || 0) > 1 && todayCompletedWorkout.sectionName && (
                      <p className="text-xs text-white/80 truncate mt-0.5">
                        {todayCompletedWorkout.sectionName}
                      </p>
                    )}
                  </div>
                  
                  <ChevronRight className="w-5 h-5 text-white/60 group-hover:text-white group-hover:translate-x-1 transition-all shrink-0" aria-hidden="true" />
                </div>
              </button>
            )}

            {/* Active workout or Up Next workout */}
            {/* When today's workout is completed, show a compact secondary CTA */}
            <button 
              onClick={() => {
                let url: string;
                if (activeWorkout) {
                  url = `/program/${activeWorkout.programId}?week=${activeWorkout.week}&workout=${activeWorkout.workoutIndex}`;
                } else {
                  clearActiveWorkout();
                  // Always use today's date, not the scheduled date - workouts should start on the current day
                  const today = format(new Date(), 'yyyy-MM-dd');
                  url = `/program/${nextWorkoutInfo!.program.id}?week=${nextWorkoutInfo!.currentWeek}&workout=${nextWorkoutInfo!.workoutIndex}&date=${today}`;
                }
                navigate(url);
              }}
              aria-label={activeWorkout 
                ? `${activeWorkout.isPaused ? 'Resume' : 'Continue'} workout: ${activeWorkout.workoutName}` 
                : `Start workout: ${nextWorkoutInfo!.program.title}${(nextWorkoutInfo!.program.sessions?.length || 0) > 1 && nextWorkoutInfo!.workoutName ? `, ${nextWorkoutInfo!.workoutName}` : ''}`}
              className={cn(
                "relative overflow-hidden hover:shadow-lg transition-all group cursor-pointer w-full text-left",
                activeWorkout?.isPaused
                  ? "rounded-2xl px-4 py-2.5 bg-gradient-to-r from-amber-400/90 to-orange-400/90 dark:from-amber-500/70 dark:to-orange-400/60 hover:shadow-amber-400/15"
                  : todayCompletedWorkout && !activeWorkout
                    ? "rounded-lg px-3 py-1.5 bg-muted/30 hover:bg-muted/50 border border-border/30"
                    : "rounded-2xl px-4 py-2.5 bg-gradient-to-r from-primary/90 to-emerald-600/90 dark:from-primary/80 dark:to-emerald-600/70 hover:shadow-primary/15"
              )}>
                {!todayCompletedWorkout && !activeWorkout?.isPaused && (
                  <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
                
                <div className={cn(
                  "flex items-center relative",
                  todayCompletedWorkout && !activeWorkout ? "gap-3" : "gap-4"
                )}>
                  {activeWorkout ? (
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        if (activeWorkout.isPaused) {
                          const updatedWorkout = { 
                            ...activeWorkout, 
                            isPaused: false, 
                            startedAt: Date.now()
                          };
                          setActiveWorkout(updatedWorkout);
                          setActiveWorkoutState(updatedWorkout);
                          navigate(`/program/${activeWorkout.programId}?week=${activeWorkout.week}&workout=${activeWorkout.workoutIndex}`);
                        } else {
                          const liveDuration = Math.floor((Date.now() - activeWorkout.startedAt) / 1000);
                          const totalElapsed = activeWorkout.elapsed + liveDuration;
                          const updatedWorkout = { 
                            ...activeWorkout, 
                            isPaused: true, 
                            elapsed: totalElapsed,
                            startedAt: Date.now()
                          };
                          setActiveWorkout(updatedWorkout);
                          setActiveWorkoutState(updatedWorkout);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label={activeWorkout.isPaused ? `Resume ${activeWorkout.workoutName}` : `Pause ${activeWorkout.workoutName}`}
                      onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.click()}
                      className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white/40 dark:bg-white/30 backdrop-blur-sm flex items-center justify-center shrink-0 hover:scale-110 hover:bg-white/50 dark:hover:bg-white/40 transition-all shadow-lg"
                      data-testid="button-home-pause-resume"
                    >
                      {activeWorkout.isPaused ? (
                        <Play className="w-7 h-7 md:w-8 md:h-8 text-white fill-white ml-0.5" aria-hidden="true" />
                      ) : (
                        <Pause className="w-7 h-7 md:w-8 md:h-8 text-white fill-white" aria-hidden="true" />
                      )}
                    </span>
                  ) : todayCompletedWorkout ? (
                    <div className="w-8 h-8 rounded-full bg-muted/60 flex items-center justify-center shrink-0 group-hover:bg-muted transition-colors" aria-hidden="true">
                      <Play className="w-4 h-4 text-muted-foreground/70 group-hover:text-muted-foreground ml-0.5" />
                    </div>
                  ) : (
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white/40 dark:bg-white/30 backdrop-blur-sm flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform shadow-lg" aria-hidden="true">
                      <Play className="w-7 h-7 md:w-8 md:h-8 text-white fill-white ml-0.5" />
                    </div>
                  )}
                  
                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                    {activeWorkout ? (
                      <>
                        <p className="text-xs uppercase tracking-wider text-white/90 leading-none mb-1">
                          {activeWorkout.isPaused ? "Paused" : "In Progress"} • {formatElapsedTime(activeWorkoutElapsed)}
                        </p>
                        <h2 className="text-base font-bold text-white truncate leading-tight">
                          {activeWorkout.workoutName}
                        </h2>
                      </>
                    ) : todayCompletedWorkout ? (
                      <div className="flex flex-col">
                        <span className="text-xs text-muted-foreground">Up next</span>
                        <span className="text-sm text-foreground truncate font-medium">
                          {nextWorkoutInfo!.program.title}
                        </span>
                        {(nextWorkoutInfo!.program.sessions?.length || 0) > 1 && nextWorkoutInfo!.workoutName && nextWorkoutInfo!.workoutName !== nextWorkoutInfo!.program.title && (
                          <span className="text-xs text-muted-foreground truncate">
                            {nextWorkoutInfo!.workoutName}
                          </span>
                        )}
                      </div>
                    ) : (
                      <>
                        <p className="text-xs uppercase tracking-wider text-white/90 leading-none mb-1">
                          {nextWorkoutInfo!.isScheduled 
                            ? `Scheduled • ${nextWorkoutInfo!.scheduledDateLabel}`
                            : "Up Next"}
                        </p>
                        <h2 className="text-base font-bold text-white truncate leading-tight">
                          {nextWorkoutInfo!.program.title}
                        </h2>
                        {(nextWorkoutInfo!.program.sessions?.length || 0) > 1 && nextWorkoutInfo!.workoutName && nextWorkoutInfo!.workoutName !== nextWorkoutInfo!.program.title && (
                          <p className="text-xs text-white/80 truncate mt-0.5">
                            {nextWorkoutInfo!.workoutName}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  
                  {activeWorkout ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-white/60 hover:text-white hover:bg-white/20 shrink-0"
                          data-testid="button-workout-menu"
                        >
                          <MoreVertical className="w-5 h-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-card border-border/50">
                        <DropdownMenuItem
                          className="text-red-400 focus:text-red-400 focus:bg-red-400/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowCancelWorkoutDialog(true);
                          }}
                          data-testid="button-cancel-workout"
                        >
                          <Ban className="w-4 h-4 mr-2" />
                          Cancel Workout
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <ChevronRight className={cn(
                      "group-hover:translate-x-0.5 transition-all shrink-0",
                      todayCompletedWorkout ? "w-4 h-4 text-muted-foreground/40 group-hover:text-muted-foreground/60" : "w-5 h-5 text-white/60 group-hover:text-white"
                    )} />
                  )}
                </div>
              </button>
          </motion.section>
        )}

        {/* Progress Info Dialogs - Beautified */}
        <Dialog open={progressPopup === 'week'} onOpenChange={(open) => !open && setProgressPopup(null)}>
          <DialogContent className="bg-gradient-to-b from-card to-card/95 border-primary/20 max-w-sm overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10 pointer-events-none" />
            <DialogHeader className="relative">
              <DialogTitle className="flex items-center gap-2 text-primary text-lg">
                <div className="p-2 rounded-full bg-primary/10">
                  <Zap className="w-5 h-5" />
                </div>
                This Week
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 relative">
              <div className="text-center py-6 bg-primary/5 rounded-xl border border-primary/10">
                <p className="text-5xl font-bold text-foreground mb-1">{progressMetrics.thisWeekWorkouts}</p>
                <p className="text-sm text-muted-foreground">{progressMetrics.thisWeekWorkouts === 1 ? "workout completed" : "workouts completed"}</p>
              </div>
              <div className="bg-gradient-to-r from-primary/10 to-emerald-500/10 rounded-xl p-4 border border-primary/20">
                <p className="text-sm text-foreground leading-relaxed">
                  {progressMetrics.thisWeekWorkouts === 0 && "Start your week strong! Your first workout is waiting."}
                  {progressMetrics.thisWeekWorkouts === 1 && "Great start! One down, keep the momentum going!"}
                  {progressMetrics.thisWeekWorkouts === 2 && "Solid progress! You're building a great habit."}
                  {progressMetrics.thisWeekWorkouts === 3 && "Halfway through a strong week! Keep pushing!"}
                  {progressMetrics.thisWeekWorkouts === 4 && "Fantastic dedication! Almost hitting 5 this week!"}
                  {progressMetrics.thisWeekWorkouts >= 5 && "You're on fire! 5+ workouts - absolute champion!"}
                </p>
              </div>
              <p className="text-xs text-muted-foreground/70 text-center">
                Week resets every Monday
              </p>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={progressPopup === 'streak'} onOpenChange={(open) => !open && setProgressPopup(null)}>
          <DialogContent className="bg-gradient-to-b from-card to-card/95 border-orange-400/20 max-w-sm overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-400/5 via-transparent to-orange-400/10 pointer-events-none" />
            <DialogHeader className="relative">
              <DialogTitle className="flex items-center gap-2 text-orange-400 text-lg">
                <div className="p-2 rounded-full bg-orange-400/10">
                  <Flame className="w-5 h-5" />
                </div>
                Week Streak
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 relative">
              <div className="text-center py-6 bg-orange-400/5 rounded-xl border border-orange-400/10">
                <p className="text-5xl font-bold text-foreground mb-1">{progressMetrics.activeWeeks}</p>
                <p className="text-sm text-muted-foreground">{progressMetrics.activeWeeks === 1 ? "week streak" : "week streak"}</p>
              </div>
              <div className="bg-gradient-to-r from-orange-400/10 to-amber-400/10 rounded-xl p-4 border border-orange-400/20">
                <p className="text-sm text-foreground leading-relaxed">
                  {progressMetrics.activeWeeks === 0 && "Complete a workout to start your streak!"}
                  {progressMetrics.activeWeeks === 1 && "1 week streak! Keep it going next week."}
                  {progressMetrics.activeWeeks >= 2 && progressMetrics.activeWeeks < 5 && `${progressMetrics.activeWeeks} week streak! You're building momentum.`}
                  {progressMetrics.activeWeeks >= 5 && progressMetrics.activeWeeks < 10 && `${progressMetrics.activeWeeks} week streak! You're on fire!`}
                  {progressMetrics.activeWeeks >= 10 && progressMetrics.activeWeeks < 20 && `${progressMetrics.activeWeeks} week streak! Consistency is your superpower.`}
                  {progressMetrics.activeWeeks >= 20 && progressMetrics.activeWeeks < 40 && `${progressMetrics.activeWeeks} week streak! Unstoppable!`}
                  {progressMetrics.activeWeeks >= 40 && `${progressMetrics.activeWeeks} week streak! Legendary dedication!`}
                </p>
              </div>
              <p className="text-xs text-muted-foreground/70 text-center">
                Consecutive weeks with at least 1 workout (Mon-Sun)
              </p>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={progressPopup === 'total'} onOpenChange={(open) => !open && setProgressPopup(null)}>
          <DialogContent className="bg-gradient-to-b from-card to-card/95 border-emerald-400/20 max-w-sm overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-400/5 via-transparent to-emerald-400/10 pointer-events-none" />
            <DialogHeader className="relative">
              <DialogTitle className="flex items-center gap-2 text-emerald-400 text-lg">
                <div className="p-2 rounded-full bg-emerald-400/10">
                  <Award className="w-5 h-5" />
                </div>
                All-Time Workouts
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 relative">
              <div className="text-center py-6 bg-emerald-400/5 rounded-xl border border-emerald-400/10">
                <p className="text-5xl font-bold text-foreground mb-1">{progressMetrics.totalWorkouts}</p>
                <p className="text-sm text-muted-foreground">{progressMetrics.totalWorkouts === 1 ? "workout" : "workouts"} completed</p>
              </div>
              <div className="bg-gradient-to-r from-emerald-400/10 to-teal-400/10 rounded-xl p-4 border border-emerald-400/20">
                <p className="text-sm text-foreground leading-relaxed">
                  {progressMetrics.totalWorkouts === 0 && "Your journey starts now! Complete your first workout."}
                  {progressMetrics.totalWorkouts >= 1 && progressMetrics.totalWorkouts < 10 && "Great start! Every workout counts."}
                  {progressMetrics.totalWorkouts >= 10 && progressMetrics.totalWorkouts < 25 && "Double digits! You're making real progress."}
                  {progressMetrics.totalWorkouts >= 25 && progressMetrics.totalWorkouts < 50 && "25+ workouts! Your dedication is showing."}
                  {progressMetrics.totalWorkouts >= 50 && progressMetrics.totalWorkouts < 100 && "50+ workouts! You're a fitness warrior!"}
                  {progressMetrics.totalWorkouts >= 100 && "100+ workouts! Absolute legend status!"}
                </p>
              </div>
              <p className="text-xs text-muted-foreground/70 text-center">
                Every workout is a step toward your goals
              </p>
            </div>
          </DialogContent>
        </Dialog>

        {/* ACTIVE PROGRAMS - Shows all current programs except the recommended one */}
        {(currentPrograms.filter((p: Program) => nextWorkoutInfo?.program.id !== p.id).length > 0 || inactiveCurrentPrograms.length > 0) && (
          <section className="flex flex-col min-h-0 shrink-0">
            <div className="mb-1 shrink-0">
              <div className="flex items-center justify-between">
                <h2 className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Active Programs</h2>
                <Link href="/my-programs" className="text-xs text-muted-foreground hover:text-primary transition-colors">View all</Link>
              </div>
            </div>
            
            <div className="space-y-2">
              {currentPrograms
                .filter((program: Program) => nextWorkoutInfo?.program.id !== program.id)
                .map((program: Program) => {
                const progress = getProgramProgress(program);
                const nextSession = progress.weekWorkouts[progress.currentWorkout]?.session;
                
                return (
                  <Link key={program.id} href={`/program/${program.id}`}>
                    <div className="rounded-xl border p-3 md:p-2.5 transition-all cursor-pointer group hover:border-primary/40 bg-card/40 border-border/30">
                      <div className="flex items-center gap-3">
                        <div className="relative shrink-0">
                          <ProgramImage 
                            image={program.image || ''} 
                            title={program.title}
                            className="w-12 h-12 md:w-10 md:h-10 rounded-lg overflow-hidden"
                            iconSize="w-5 h-5"
                          />
                          <div className="absolute -bottom-1 -right-1 w-5 h-5 md:w-4 md:h-4 rounded-full bg-primary/90 flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform">
                            <Play className="w-2.5 h-2.5 md:w-2 md:h-2 text-primary-foreground fill-primary-foreground ml-0.5" />
                          </div>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <h3 className="text-sm md:text-xs font-medium text-foreground truncate group-hover:text-primary transition-colors">{program.title}</h3>
                            {(program as any).badgeStatus === 'new' && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-green-500/20 text-green-500 rounded shrink-0">NEW</span>
                            )}
                            {(program as any).badgeStatus === 'updated' && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-blue-500/20 text-blue-500 rounded shrink-0">UPDATED</span>
                            )}
                          </div>
                          <p className="text-sm md:text-xs text-muted-foreground truncate">
                            {(() => {
                              const sessionName = nextSession?.name;
                              // Show session name only if it's different from the program title
                              if (sessionName && sessionName !== program.title) {
                                return `Next: ${sessionName}`;
                              }
                              // Otherwise show last activity
                              if (progress.lastActivity) {
                                const daysAgo = Math.floor((Date.now() - new Date(progress.lastActivity).getTime()) / (1000 * 60 * 60 * 24));
                                if (daysAgo === 0) return "Active today";
                                if (daysAgo === 1) return "Last active yesterday";
                                return `Last active ${daysAgo} days ago`;
                              }
                              return "Ready to start";
                            })()}
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.preventDefault()}>
                          <button 
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(program.id); }} 
                            className="p-2 text-muted-foreground hover:text-amber-400 transition-colors rounded-lg hover:bg-amber-400/10"
                            data-testid={`button-favorite-${program.id}`}
                          >
                            <Star className={cn("w-5 h-5 md:w-4 md:h-4", isFavorite(program.id) && "fill-amber-400 text-amber-400")} />
                          </button>
                          <button 
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); openStopDialog(program); }} 
                            className="p-2 text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted/50"
                            data-testid={`button-stop-${program.id}`}
                          >
                            <X className="w-5 h-5 md:w-4 md:h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Inactive Programs */}
            {inactiveCurrentPrograms.length > 0 && (
              <div className="mt-2 pt-2 border-t border-border/20">
                <button
                  onClick={(e) => { e.stopPropagation(); setShowInactive(!showInactive); }}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="button-toggle-inactive"
                >
                  {showInactive ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {showInactive ? "Hide" : "Show"} inactive ({inactiveCurrentPrograms.length})
                </button>
                <AnimatePresence>
                  {showInactive && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-2 mt-2">
                        {inactiveCurrentPrograms.map((program: Program) => (
                          <div key={program.id} className="rounded-lg border border-border/20 bg-card/20 p-2.5 opacity-60">
                            <div className="flex items-center gap-2">
                              <ProgramImage 
                              image={program.image || ''} 
                              title={program.title}
                              className="w-8 h-8 rounded overflow-hidden shrink-0"
                              iconSize="w-4 h-4"
                            />
                              <div className="flex-1 min-w-0">
                                <h4 className="text-xs font-medium text-foreground truncate">{program.title}</h4>
                                <p className="text-xs text-muted-foreground">Inactive</p>
                              </div>
                              <button onClick={() => openStopDialog(program)} className="p-1 text-muted-foreground hover:text-foreground">
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </section>
        )}

        {/* Empty state - show when no current programs AND no active training block AND no active workout (but not while loading) */}
        {!isProgramsLoading && !isUserProgramsLoading && currentPrograms.length === 0 && inactiveCurrentPrograms.length === 0 && !activeBlock && !activeWorkout && (
          <section className="bg-gradient-to-br from-primary/5 to-emerald-500/5 rounded-xl border border-primary/20 p-4 shrink-0">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                <Flame className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <h2 className="text-base font-semibold text-foreground">Start your fitness journey</h2>
                <p className="text-sm text-muted-foreground">Choose how you'd like to begin</p>
              </div>
              <Button size="sm" onClick={startTutorial} className="h-7 text-xs px-3 bg-primary shrink-0" data-testid="button-onboarding-tutorial">
                Tutorial
              </Button>
            </div>
            
            <div className="grid gap-2.5">
              <Link href="/programs">
                <button
                  className="w-full flex items-center gap-3 p-3 rounded-lg bg-card/80 border border-border/50 hover:border-primary/50 transition-all text-left group"
                  data-testid="button-get-started-browse"
                >
                  <div className="w-9 h-9 rounded-lg bg-amber-500/20 flex items-center justify-center shrink-0">
                    <Dumbbell className="w-4 h-4 text-amber-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">Browse Expert Programs</p>
                    <p className="text-xs text-muted-foreground">{apiPrograms.length} professionally designed workouts</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </button>
              </Link>
              
              <Link href="/create">
                <button
                  className="w-full flex items-center gap-3 p-3 rounded-lg bg-card/80 border border-border/50 hover:border-blue-500/50 transition-all text-left group"
                  data-testid="button-get-started-create"
                >
                  <div className="w-9 h-9 rounded-lg bg-blue-500/20 flex items-center justify-center shrink-0">
                    <Plus className="w-4 h-4 text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground group-hover:text-blue-400 transition-colors">Build Your Own Program</p>
                    <p className="text-xs text-muted-foreground">Create a custom workout</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </button>
              </Link>
              
              <button
                onClick={() => setShowTrainingBlockDialog(true)}
                className="w-full flex items-center gap-3 p-3 rounded-lg bg-card/80 border border-border/50 hover:border-purple-500/50 transition-all text-left group"
                data-testid="button-get-started-block"
              >
                <div className="w-9 h-9 rounded-lg bg-purple-500/20 flex items-center justify-center shrink-0">
                  <Target className="w-4 h-4 text-purple-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground group-hover:text-purple-400 transition-colors">Start a Training Block</p>
                  <p className="text-xs text-muted-foreground">Plan your training phases</p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
              </button>
            </div>
          </section>
        )}

        {/* Compact "Add a program" prompt when user has training block but no programs and no active workout */}
        {!isProgramsLoading && !isUserProgramsLoading && currentPrograms.length === 0 && inactiveCurrentPrograms.length === 0 && activeBlock && !activeWorkout && (
          <section className="shrink-0">
            <Link href="/programs">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-card/40 border border-border/30 hover:border-primary/40 transition-all group cursor-pointer">
                <div className="w-9 h-9 rounded-lg bg-amber-500/20 flex items-center justify-center shrink-0">
                  <Dumbbell className="w-4 h-4 text-amber-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">Add a workout program</p>
                  <p className="text-xs text-muted-foreground">Browse {apiPrograms.length} expert programs</p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
              </div>
            </Link>
          </section>
        )}

        {/* Saved Drafts Section */}
        {drafts.length > 0 && (
          <section className="shrink-0">
            <div className="mb-2 shrink-0">
              <div className="flex items-center justify-between">
                <h2 className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Saved Drafts</h2>
                <Link href="/create" className="text-xs text-muted-foreground hover:text-primary transition-colors">New Program</Link>
              </div>
            </div>
            <div className="space-y-2">
              {drafts.slice(0, 3).map((draft: ProgramDraft) => (
                <div 
                  key={draft.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-card/40 border border-border/30 hover:border-primary/40 transition-all group"
                  data-testid={`draft-card-${draft.id}`}
                >
                  <div className="w-9 h-9 rounded-lg bg-amber-500/20 flex items-center justify-center shrink-0">
                    <Dumbbell className="w-4 h-4 text-amber-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-foreground truncate">{draft.name || "Untitled Draft"}</h3>
                    <p className="text-xs text-muted-foreground">
                      {draft.savedAt ? format(new Date(draft.savedAt), "MMM d 'at' h:mma") : "Draft"}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Link href={`/create?draftId=${draft.id}`}>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-primary"
                        data-testid={`button-edit-draft-${draft.id}`}
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </Button>
                    </Link>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => setDraftToDelete(draft.id)}
                      data-testid={`button-delete-draft-${draft.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
              {drafts.length > 3 && (
                <Link href="/create" className="block text-center text-xs text-muted-foreground hover:text-primary py-2">
                  +{drafts.length - 3} more drafts
                </Link>
              )}
            </div>
          </section>
        )}

        {/* Delete Draft Confirmation */}
        <AlertDialog open={!!draftToDelete} onOpenChange={(open) => !open && setDraftToDelete(null)}>
          <AlertDialogContent className="bg-card border-border max-w-sm">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-foreground">Delete Draft?</AlertDialogTitle>
              <AlertDialogDescription className="text-muted-foreground">
                This will permanently delete this draft. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-border/50">Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => {
                  if (draftToDelete) {
                    removeDraft(draftToDelete);
                    setDraftToDelete(null);
                  }
                }}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Stop Program Dialog */}
        <AlertDialog open={showStopDialog} onOpenChange={setShowStopDialog}>
          <AlertDialogContent className="bg-card border-border max-w-sm">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-foreground flex items-center gap-2 text-base">
                <Pause className="w-4 h-4 text-primary" /> Stop Program
              </AlertDialogTitle>
              <AlertDialogDescription className="text-muted-foreground text-sm">
                What would you like to do with <span className="text-foreground font-medium">{stopDialogProgram?.title}</span>?
              </AlertDialogDescription>
            </AlertDialogHeader>
            
            {/* Warnings */}
            {stopDialogProgram && (
              <>
                {getScheduledSessionsForProgram(stopDialogProgram.id).length > 0 && (
                  <div className="flex items-start gap-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs">
                    <AlertTriangle className="w-4 h-4 text-amber-500 dark:text-amber-400 shrink-0 mt-0.5" />
                    <p className="text-amber-700 dark:text-amber-300">
                      {getScheduledSessionsForProgram(stopDialogProgram.id).length} scheduled workout{getScheduledSessionsForProgram(stopDialogProgram.id).length > 1 ? 's' : ''} will be removed if you archive or remove this program.
                    </p>
                  </div>
                )}
                {getTrainingBlocksForProgram(stopDialogProgram.id).length > 0 && (
                  <div className="flex items-start gap-2 p-2 rounded-lg bg-blue-500/10 border border-blue-500/20 text-xs">
                    <Target className="w-4 h-4 text-blue-500 dark:text-blue-400 shrink-0 mt-0.5" />
                    <p className="text-blue-700 dark:text-blue-300">
                      This program is part of {getTrainingBlocksForProgram(stopDialogProgram.id).length} training block{getTrainingBlocksForProgram(stopDialogProgram.id).length > 1 ? 's' : ''}: {getTrainingBlocksForProgram(stopDialogProgram.id).map(b => b.name).join(', ')}
                    </p>
                  </div>
                )}
              </>
            )}
            
            <div className="space-y-2 py-2">
              <button
                onClick={() => handleStopProgram('keep_favorite')}
                className="w-full flex items-center gap-3 p-3 rounded-lg bg-card/50 border border-border/50 hover:border-amber-500/50 transition-colors text-left"
                data-testid="button-stop-keep-favorite"
              >
                <Star className="w-4 h-4 text-amber-400" />
                <div>
                  <p className="text-sm font-medium text-foreground">Keep in Favorites</p>
                  <p className="text-xs text-muted-foreground">Save to favorites and return later</p>
                </div>
              </button>
              <button
                onClick={() => handleStopProgram('archive')}
                className="w-full flex items-center gap-3 p-3 rounded-lg bg-card/50 border border-border/50 hover:border-blue-500/50 transition-colors text-left"
                data-testid="button-stop-archive"
              >
                <Archive className="w-4 h-4 text-blue-400" />
                <div>
                  <p className="text-sm font-medium text-foreground">Archive</p>
                  <p className="text-xs text-muted-foreground">Move to archive (can restore later)</p>
                </div>
              </button>
              <button
                onClick={() => handleStopProgram('remove')}
                className="w-full flex items-center gap-3 p-3 rounded-lg bg-card/50 border border-border/50 hover:border-destructive/50 transition-colors text-left"
                data-testid="button-stop-remove"
              >
                <Trash2 className="w-4 h-4 text-destructive" />
                <div>
                  <p className="text-sm font-medium text-foreground">Remove</p>
                  <p className="text-xs text-muted-foreground">
                    {stopDialogProgram && isCustomProgram(stopDialogProgram) ? "Archive custom program" : "Return to available programs"}
                  </p>
                </div>
              </button>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel className="h-8 text-sm">Cancel</AlertDialogCancel>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        
        {/* Training Block Completion Celebration Dialog */}
        <Dialog open={!!recentlyCompletedBlock} onOpenChange={(open) => {
          if (!open && recentlyCompletedBlock) {
            celebrateBlock(recentlyCompletedBlock.id);
          }
        }}>
          <DialogContent className="sm:max-w-[400px]">
            {recentlyCompletedBlock && completedBlockStats && (
              <>
                <DialogHeader className="text-center pb-2">
                  <div className="mx-auto mb-3 w-16 h-16 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                    <Award className="w-8 h-8 text-primary" />
                  </div>
                  <DialogTitle className="text-xl">Training Block Complete!</DialogTitle>
                  <p className="text-muted-foreground text-sm mt-1">
                    Great work finishing <span className="font-medium text-foreground">{recentlyCompletedBlock.name}</span>
                  </p>
                </DialogHeader>
                
                <div className="grid grid-cols-3 gap-3 py-4">
                  <div className="text-center p-3 rounded-lg bg-muted/30">
                    <div className="text-2xl font-bold text-primary">{completedBlockStats.workoutsCompleted}</div>
                    <div className="text-xs text-muted-foreground">Workouts</div>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-muted/30">
                    <div className="text-2xl font-bold text-foreground">{completedBlockStats.totalWeeks}</div>
                    <div className="text-xs text-muted-foreground">{completedBlockStats.totalWeeks === 1 ? 'Week' : 'Weeks'}</div>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-muted/30">
                    <div className="text-2xl font-bold text-foreground">{completedBlockStats.programCount}</div>
                    <div className="text-xs text-muted-foreground">{completedBlockStats.programCount === 1 ? 'Program' : 'Programs'}</div>
                  </div>
                </div>
                
                <div className="space-y-2 pt-2">
                  {upcomingBlocks.length > 0 ? (
                    <>
                      <p className="text-sm text-muted-foreground text-center mb-3">
                        Your next training block starts {format(new Date(upcomingBlocks[0].startDate), "MMM d")}
                      </p>
                      <Button 
                        className="w-full" 
                        onClick={() => {
                          celebrateBlock(recentlyCompletedBlock.id);
                          navigate(`/training-block/${upcomingBlocks[0].id}`);
                        }}
                        data-testid="button-view-next-block"
                      >
                        <ChevronRight className="w-4 h-4 mr-2" />
                        View {upcomingBlocks[0].name}
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground text-center mb-3">
                        Ready for your next challenge?
                      </p>
                      <Button 
                        className="w-full" 
                        onClick={() => {
                          celebrateBlock(recentlyCompletedBlock.id);
                          setShowTrainingBlockDialog(true);
                        }}
                        data-testid="button-create-new-block"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Create New Training Block
                      </Button>
                    </>
                  )}
                  <Button 
                    variant="ghost" 
                    className="w-full text-muted-foreground"
                    onClick={() => celebrateBlock(recentlyCompletedBlock.id)}
                    data-testid="button-dismiss-celebration"
                  >
                    Dismiss
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Training Block Dialog */}
        <TrainingBlocksDialog 
          open={showTrainingBlockDialog} 
          onOpenChange={(open) => {
            setShowTrainingBlockDialog(open);
            if (!open) setSelectedBlockId(null);
          }}
          initialBlockId={selectedBlockId}
        />

        {/* Cancel Workout Dialog */}
        <AlertDialog open={showCancelWorkoutDialog} onOpenChange={setShowCancelWorkoutDialog}>
          <AlertDialogContent className="bg-card border-border/50">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-foreground flex items-center gap-2">
                <Ban className="w-5 h-5 text-red-400" />
                Cancel Workout?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-muted-foreground">
                Are you sure you want to cancel this workout? Your progress will not be saved.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="h-9">Keep Working Out</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleCancelWorkout}
                className="bg-red-500 hover:bg-red-600 text-white"
                data-testid="button-confirm-cancel-workout"
              >
                Cancel Workout
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Music Playlist Selector - Sticky at the bottom */}
        {allVisiblePlaylists.length > 0 && (
          <section className="shrink-0 mt-auto sticky bottom-0 bg-background pb-2">
            <div className="rounded-lg border border-[#1DB954]/20 bg-[#1DB954]/5 px-3 py-1.5">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 fill-[#1DB954] shrink-0" viewBox="0 0 24 24">
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
                </svg>
                <div className="flex-1 min-w-0">
                  <Select
                    value={selectedPlaylistId}
                    onValueChange={setSelectedPlaylistId}
                    disabled={isUpdating}
                  >
                    <SelectTrigger className="h-auto w-full max-w-[200px] gap-1 text-sm font-medium bg-transparent border-0 shadow-none p-0 hover:bg-transparent focus:ring-0 focus:ring-offset-0 justify-start [&>span]:truncate [&>span]:max-w-[180px]" data-testid="home-music-playlist">
                      <SelectValue>
                        <span className="text-foreground font-semibold truncate">
                          {selectedPlaylist?.name || "Select playlist"}
                        </span>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="!shadow-none border-border/50 max-h-[50vh] overflow-y-auto">
                      {visibleCurated.length > 0 && (
                        <>
                          {visibleCurated.map((genre) => (
                            <SelectItem key={genre.value} value={genre.value} data-testid={`home-playlist-${genre.value}`}>
                              {genre.label}
                            </SelectItem>
                          ))}
                        </>
                      )}
                      {customPlaylists.length > 0 && (
                        <>
                          {visibleCurated.length > 0 && <div className="border-t border-border/30 my-1" />}
                          <div className="px-2 py-1 text-xs text-muted-foreground font-medium">My Playlists</div>
                          {customPlaylists.map((playlist) => (
                            <SelectItem key={playlist.id} value={playlist.id} className="py-2.5" data-testid={`home-playlist-${playlist.id}`}>
                              <span className="flex items-center gap-1.5">
                                <Music className="w-3 h-3 text-[#1DB954]" />
                                {playlist.name}
                              </span>
                            </SelectItem>
                          ))}
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <Link
                  href="/music"
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full text-[#1DB954] hover:bg-[#1DB954]/10 transition-all shrink-0"
                  data-testid="button-manage-playlists"
                >
                  <Settings className="w-4 h-4" />
                </Link>
                <a
                  href={selectedPlaylist?.url || "https://open.spotify.com"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold bg-[#1DB954] text-white hover:bg-[#1ed760] transition-all shrink-0 no-underline"
                  data-testid="button-play-spotify"
                >
                  <ExternalLink className="w-3 h-3" />
                  <span>Open Spotify</span>
                </a>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
    </LayoutWithSidebar>
  );
}

interface ProgramProgress {
  currentWeek: number;
  currentWorkout: number;
  weekWorkouts: { session: any; dayLabel?: string }[];
  lastActivity: string | null;
}

function ProgramCard({ 
  program, 
  progress,
  isFavorite,
  isCustom,
  canDelete,
  onStop,
  onToggleFavorite,
  onDelete,
  isInactive,
  inBlock,
  navigate,
}: { 
  program: Program;
  progress: ProgramProgress;
  isFavorite: boolean;
  isCustom?: boolean;
  canDelete: boolean;
  onStop: () => void;
  onToggleFavorite: () => void;
  onDelete?: () => void;
  isInactive?: boolean;
  inBlock?: boolean;
  navigate: (to: string) => void;
}) {
  const { currentWeek, currentWorkout } = progress;
  
  const displayedWorkouts = getWeekWorkouts(program, currentWeek);

  const handleWorkoutClick = (workoutIndex: number) => {
    const progressKey = `programProgress_${program.id}`;
    const stored = localStorage.getItem(progressKey);
    const existingProgress = stored ? JSON.parse(stored) : {};
    localStorage.setItem(progressKey, JSON.stringify({
      ...existingProgress,
      currentWeek: currentWeek,
      currentWorkout: workoutIndex,
      lastActivity: new Date().toISOString(),
    }));
    navigate(`/program/${program.id}`);
  };

  return (
    <div className={cn(
      "bg-card/50 rounded-lg p-3 border border-border/50 hover:border-primary/30 transition-colors snap-start",
      isInactive && "opacity-60"
    )}>
      {/* Header row */}
      <div className="flex items-center gap-3 mb-3">
        <Link href={`/program/${program.id}`} className="shrink-0">
          <ProgramImage 
            image={program.image || ''} 
            title={program.title}
            className="w-10 h-10 rounded-lg overflow-hidden"
            iconSize="w-5 h-5"
          />
        </Link>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <Link href={`/program/${program.id}`}>
              <h3 className="font-medium text-foreground text-sm truncate hover:text-primary transition-colors cursor-pointer">{program.title}</h3>
            </Link>
            {isCustom && <span className="text-xs px-1 py-0.5 bg-blue-500/20 text-blue-400 rounded shrink-0">Custom</span>}
            {(program as any).badgeStatus === 'new' && (
              <span className="text-[10px] px-1.5 py-0.5 bg-green-500/20 text-green-500 rounded shrink-0">NEW</span>
            )}
            {(program as any).badgeStatus === 'updated' && (
              <span className="text-[10px] px-1.5 py-0.5 bg-blue-500/20 text-blue-500 rounded shrink-0">UPDATED</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <button onClick={onToggleFavorite} className="p-1.5 text-muted-foreground hover:text-amber-400 transition-colors" data-testid={`button-favorite-${program.id}`}>
            <Star className={cn("w-3.5 h-3.5", isFavorite && "fill-amber-400 text-amber-400")} />
          </button>
          <button onClick={onStop} className="p-1.5 text-muted-foreground hover:text-foreground transition-colors" data-testid={`button-stop-${program.id}`}>
            <X className="w-3.5 h-3.5" />
          </button>
          {canDelete && (
            <button onClick={onDelete} className="p-1.5 text-muted-foreground hover:text-destructive transition-colors" data-testid={`button-delete-${program.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Workout list - responsive grid */}
      <div className="grid grid-cols-3 gap-1.5">
        {displayedWorkouts.length > 0 ? (
          displayedWorkouts.slice(0, 6).map((workout, index) => {
            const isNext = index === currentWorkout;
            return (
              <button
                key={index}
                onClick={() => handleWorkoutClick(index)}
                className={cn(
                  "px-2 py-1.5 rounded-lg border transition-all text-left",
                  isNext
                    ? "bg-primary/20 border-primary text-foreground"
                    : "bg-card/30 border-border/50 text-muted-foreground hover:border-primary/50 hover:text-foreground"
                )}
                data-testid={`button-workout-${program.id}-w${currentWeek}-${index}`}
              >
                <div className="flex items-center gap-1 mb-0.5">
                  {isNext && <Play className="w-2.5 h-2.5 text-primary fill-primary shrink-0" />}
                  <span className="text-[11px] font-medium truncate">
                    {workout.session?.name || `Workout ${String.fromCharCode(65 + index)}`}
                  </span>
                </div>
                <p className="text-[10px] opacity-70 truncate">
                  {isNext ? "Up next" : (workout.dayLabel || `Day ${index + 1}`)}
                </p>
              </button>
            );
          })
        ) : (
          <Link href={`/program/${program.id}`} className="col-span-3">
            <button
              className="w-full px-5 py-4 rounded-xl border-2 bg-primary/20 border-primary text-foreground text-left"
              data-testid={`button-workout-${program.id}-0`}
            >
              <div className="flex items-center gap-3 mb-1">
                <Play className="w-6 h-6 text-primary fill-primary" />
                <span className="text-base md:text-lg font-semibold">
                  {program.title}
                </span>
              </div>
              <p className="text-sm opacity-70">Up next</p>
            </button>
          </Link>
        )}
      </div>
    </div>
  );
}

function DiscoverCard({ program, onStart, onFavorite }: { program: Program; onStart: () => void; onFavorite: () => void }) {
  const exerciseCount = program.sections 
    ? program.sections.reduce((acc, s) => acc + s.exercises.length, 0)
    : program.sessions?.reduce((acc, sess) => acc + sess.sections.reduce((a, s) => a + s.exercises.length, 0), 0) || 0;

  return (
    <div className="w-40 shrink-0 rounded-lg overflow-hidden bg-card border border-border/50 hover:border-primary/30 transition-colors">
      <div className="relative h-20">
        <ProgramImage 
          image={program.image || ''} 
          title={program.title}
          className="w-full h-full"
          iconSize="w-8 h-8"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
      </div>
      <div className="p-2">
        <h4 className="font-medium text-foreground text-xs line-clamp-1 mb-1">{program.title}</h4>
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
          <span>{program.duration}</span>
          <span>{exerciseCount} ex</span>
        </div>
        <div className="flex gap-1">
          <Button size="default" className="flex-1 h-10 text-base bg-primary" onClick={onStart} data-testid={`button-start-${program.id}`}>
            <Play className="w-5 h-5 mr-2" /> Start
          </Button>
          <Button size="default" variant="outline" className="h-10 px-3" onClick={onFavorite} data-testid={`button-favorite-discover-${program.id}`}>
            <Star className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
